import { useState, useEffect, useCallback, useRef } from "react";
import PackagingProgress from "../components/PackagingProgress";
import ShiftNotes from "../components/ShiftNotes";
import SignatureCanvas from "../components/SignatureCanvas";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

import { showError } from "../lib/errorHandler";
import { openPackingSlip } from "../lib/packingSlip";
import { openBoxLabel, openBoxLabels } from "../lib/boxLabel";
import { openShiftReport } from "../lib/shiftReport";

import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Play,
  Square,
  ArrowLeftRight,
  Pause,
  Droplets,
  Wrench,
  ChevronLeft,
  Clock,
  Factory,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Plus,
  Minus,
  AlertTriangle,
  ClipboardList,
  Package,
  Scale,
  QrCode,
  CheckSquare,
  FileText,
  History,
} from "lucide-react";
import { WeightHistoryDialog } from "../components/WeightHistoryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  running: { label: "EM EXECUÇÃO", color: "bg-green-600", barColor: "bg-green-500" },
  paused: { label: "PAUSADA", color: "bg-amber-600", barColor: "bg-amber-500" },
  idle: { label: "SEM ORDEM", color: "bg-slate-500", barColor: "bg-slate-400" },
  stopped: { label: "PARAGEM NÃO PLANEADA", color: "bg-red-600", barColor: "bg-red-500" },
  maintenance: { label: "MANUTENÇÃO", color: "bg-purple-600", barColor: "bg-purple-500" },
  setup: { label: "SETUP", color: "bg-blue-600", barColor: "bg-blue-500" },
};

// ===== OPERATOR AVATAR =====
function OperatorAvatar({ photo, name, code, size = "sm" }) {
  const px = size === "xl" ? "h-24 w-24 text-3xl" : size === "lg" ? "h-14 w-14 text-lg" : size === "md" ? "h-10 w-10 text-sm" : "h-7 w-7 text-[10px]";
  const initials = (name || code || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  if (photo) {
    return <img src={photo} alt={name || code || "op"} className={`${px} rounded-full object-cover border-2 border-white shadow-sm shrink-0`} />;
  }
  return (
    <div className={`${px} rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shrink-0 border-2 border-white shadow-sm`}>
      {initials}
    </div>
  );
}

// ===== MACHINE GRID VIEW =====
function MachineGrid({ machines, onSelect }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {machines.map((m) => {
        const machine = m.machine;
        const order = m.active_order;
        const statusCfg = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;
        const completedOrders = m.completed_count || 0;
        const totalOrders = m.total_orders || 0;

        return (
          <div
            key={machine.id}
            onClick={() => onSelect(m)}
            className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-amber-400 transition-all"
            data-testid={`op-machine-${machine.code}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono font-black text-xl text-slate-900">{machine.code}</span>
              <span className={`h-3 w-3 rounded-full ${statusCfg.color}`} />
            </div>
            <p className="text-xs text-slate-500 mb-2 truncate">{machine.description} | {machine.tonnage}T</p>
            
            {m.operator && (
              <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-2 flex items-center gap-2">
                <OperatorAvatar photo={m.operator.photo_url} name={m.operator.name} code={m.operator.user_code} size="sm" />
                <p className="text-xs text-blue-700 font-medium truncate">{m.operator.name || m.operator.user_code}</p>
              </div>
            )}

            <div className="space-y-2">
              <div>
                <p className="text-xs text-slate-400 font-medium">Ordem atual:</p>
                {order ? (
                  <p className="text-sm font-semibold text-slate-800">{order.code}</p>
                ) : (
                  <p className="text-xs text-slate-400 italic">Nenhuma ordem para execução</p>
                )}
              </div>
              <div className="border-t border-slate-100 pt-2">
                <p className="text-xs text-slate-400 font-medium">Ordens concluídas:</p>
                <p className="text-sm font-mono text-slate-600">{completedOrders} de {totalOrders}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== AVAILABILITY TIMELINE =====
function AvailabilityTimeline({ stops, startedAt }) {
  const now = new Date();
  const start = startedAt ? new Date(startedAt) : new Date(now.getTime() - 8 * 3600000);
  const totalMs = now.getTime() - start.getTime();
  if (totalMs <= 0) return null;

  // Build segments
  const segments = [];
  let lastEnd = start.getTime();

  const sortedStops = [...stops].sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime());

  for (const stop of sortedStops) {
    const stopStart = new Date(stop.started_at).getTime();
    const stopEnd = stop.ended_at ? new Date(stop.ended_at).getTime() : now.getTime();

    if (stopStart > lastEnd) {
      segments.push({ type: "running", start: lastEnd, end: stopStart });
    }
    segments.push({ type: "stopped", start: Math.max(stopStart, lastEnd), end: stopEnd, reason: stop.reason });
    lastEnd = stopEnd;
  }
  if (lastEnd < now.getTime()) {
    segments.push({ type: "running", start: lastEnd, end: now.getTime() });
  }

  // Time labels
  const hours = Math.ceil(totalMs / 3600000);
  const labels = [];
  for (let i = 0; i <= Math.min(hours, 12); i += 2) {
    labels.push({ time: new Date(start.getTime() + i * 3600000), pct: (i * 3600000 / totalMs) * 100 });
  }

  return (
    <div className="mt-4">
      <p className="text-xs font-semibold text-slate-500 mb-2">Gráfico de disponibilidade</p>
      <div className="relative h-8 bg-slate-200 rounded overflow-hidden">
        {segments.map((seg, i) => {
          const left = ((seg.start - start.getTime()) / totalMs) * 100;
          const width = ((seg.end - seg.start) / totalMs) * 100;
          return (
            <div
              key={i}
              className={`absolute top-0 h-full ${seg.type === "running" ? "bg-green-500" : "bg-red-500"}`}
              style={{ left: `${left}%`, width: `${Math.max(width, 0.3)}%` }}
              title={seg.type === "stopped" ? `Paragem: ${seg.reason || "N/D"}` : "Em execução"}
            />
          );
        })}
      </div>
      <div className="relative h-4 mt-1">
        {labels.map((l, i) => (
          <span key={i} className="absolute text-[10px] text-slate-400 -translate-x-1/2" style={{ left: `${l.pct}%` }}>
            {l.time.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
          </span>
        ))}
        <span className="absolute right-0 text-[10px] text-amber-500 font-bold">Agora</span>
      </div>
    </div>
  );
}

// ===== MACHINE DETAIL VIEW =====
function MachineDetail({ data, onBack, onRefresh }) {
  const { user } = useAuth();
  const { machine, active_order, part, mold, operator, production, quality, stops, completed_orders, all_orders } = data;
  const statusCfg = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;

  const [showProductionDialog, setShowProductionDialog] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showManualStopDialog, setShowManualStopDialog] = useState(false);
  const [manualStopStart, setManualStopStart] = useState("");
  const [manualStopEnd, setManualStopEnd] = useState("");
  const [manualStopReason, setManualStopReason] = useState("");
  const [manualStopNotes, setManualStopNotes] = useState("");
  const [showPurgaDialog, setShowPurgaDialog] = useState(false);
  const [showManutDialog, setShowManutDialog] = useState(false);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [showWeightDialog, setShowWeightDialog] = useState(false);
  const [showQualityDialog, setShowQualityDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showTecQualidadeDialog, setShowTecQualidadeDialog] = useState(false);
  const [showShiftWeightDialog, setShowShiftWeightDialog] = useState(false);
  const [shiftWeight1, setShiftWeight1] = useState("");
  const [shiftWeight10, setShiftWeight10] = useState("");
  const [shiftWeightNotes, setShiftWeightNotes] = useState("");
  const [showBoxLabelDialog, setShowBoxLabelDialog] = useState(false);
  const [boxQty, setBoxQty] = useState("");
  const [boxWeight, setBoxWeight] = useState("");
  const [boxNotes, setBoxNotes] = useState("");
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [clPreItems, setClPreItems] = useState([]);
  const [clSampleItems, setClSampleItems] = useState([]);
  const [clWeight10, setClWeight10] = useState("");
  const [clApprovedBy, setClApprovedBy] = useState("");
  const [clNotes, setClNotes] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [weightAlert, setWeightAlert] = useState(false);
  const [qualityAlert, setQualityAlert] = useState(false);
  const [goodQty, setGoodQty] = useState(0);
  const [rejectedQty, setRejectedQty] = useState(0);
  const [defectType, setDefectType] = useState("");
  const [stopReason, setStopReason] = useState("");
  const [stopNotes, setStopNotes] = useState("");
  const [purgaKg, setPurgaKg] = useState("");
  const [purgaNotes, setPurgaNotes] = useState("");
  const [manutNotes, setManutNotes] = useState("");
  const [weightValue, setWeightValue] = useState("");
  const [weightResult, setWeightResult] = useState(null);
  const [shotWeightValue, setShotWeightValue] = useState("");
  const [showWeightHistory, setShowWeightHistory] = useState(false);
  const [weightCheck, setWeightCheck] = useState(null);
  const [shotWeightResult, setShotWeightResult] = useState(null);
  const [cycleTimeValue, setCycleTimeValue] = useState("");
  const [updateMoldCycle, setUpdateMoldCycle] = useState(false);
  const [qualityDefects, setQualityDefects] = useState("");
  const [qualitySampleSize, setQualitySampleSize] = useState("5");
  const [qualityDefectCount, setQualityDefectCount] = useState("0");
  const [qualityNotes, setQualityNotes] = useState("");
  const [rejectQty, setRejectQty] = useState(1);
  const [rejectDefects, setRejectDefects] = useState([]);
  const [rejectNotes, setRejectNotes] = useState("");
  const [tecQualData, setTecQualData] = useState({
    visual_cor: "", visual_brilho: "", visual_impurezas: "", visual_bolhas: "", visual_manchas: "", visual_rebarbas: "", visual_preenchimento: "",
    dim_espessura: "", dim_diametro: "", dim_comprimento: "", dim_instrumento: "Calibre",
    mec_tracao: "", mec_impacto: "", mec_estanqueidade: "",
    peso_valor: "", peso_uniforme: "",
    quim_fluidez: "",
    resultado_global: "conforme",
    observacoes: "",
  });
  const [stopReasons, setStopReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // ===== ALARME REABASTECIMENTO MATÉRIA-PRIMA (1h30) =====
  const [refillAlarm, setRefillAlarm] = useState(false);      // modal aberto
  const [refillMuted, setRefillMuted] = useState(false);      // silenciado
  const refillIntervalRef = useRef(null);   // interval do próximo disparo
  const refillLoopRef = useRef(null);       // loop de som contínuo
  const refillCtxRef = useRef(null);

  const stopRefillSound = useCallback(() => {
    if (refillLoopRef.current) { clearInterval(refillLoopRef.current); refillLoopRef.current = null; }
    if (refillCtxRef.current) { try { refillCtxRef.current.close(); } catch(_) { /* ignore */ } refillCtxRef.current = null; }
  }, []);

  const startRefillSound = useCallback(() => {
    stopRefillSound();
    const beep = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        refillCtxRef.current = ctx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "square";
        gain.gain.value = 0.9;   // volume máximo
        osc.frequency.value = 1200;
        osc.start();
        setTimeout(() => { try { osc.frequency.value = 800; } catch(_) { /* ignore */ } }, 200);
        setTimeout(() => { try { osc.frequency.value = 1200; } catch(_) { /* ignore */ } }, 400);
        setTimeout(() => { try { osc.frequency.value = 800; } catch(_) { /* ignore */ } }, 600);
        setTimeout(() => { try { osc.stop(); ctx.close(); } catch(_) { /* ignore */ } }, 900);
      } catch(_) { /* ignore */ }
    };
    beep();
    refillLoopRef.current = setInterval(beep, 1500);
  }, [stopRefillSound]);

  const triggerRefillAlarm = useCallback(() => {
    setRefillAlarm(true);
    setRefillMuted(false);
    startRefillSound();
    try {
      if ("vibrate" in navigator) navigator.vibrate([500, 200, 500, 200, 800]);
    } catch(_) { /* ignore */ }
  }, [startRefillSound]);

  const confirmRefillDone = useCallback(async () => {
    stopRefillSound();
    setRefillAlarm(false);
    setRefillMuted(false);
    // registar acção para rastreabilidade
    try {
      await axios.post(`${API}/production-records/refill-log`, {
        machine_id: machine?.id,
        order_id: active_order?.id,
      });
    } catch(_) { /* ignore */ }
    toast.success("✓ Matéria-prima reabastecida - próximo aviso em 1h30");
  }, [stopRefillSound, machine?.id, active_order?.id]);

  const silenceRefillAlarm = useCallback(() => {
    stopRefillSound();
    setRefillMuted(true);
    // Toca de novo em 1 minuto se ainda não confirmou
    setTimeout(() => {
      if (refillAlarm && refillMuted) startRefillSound();
    }, 60 * 1000);
  }, [stopRefillSound, startRefillSound, refillAlarm, refillMuted]);

  // Timer 1h30 (90 min) - só quando ordem em execução
  useEffect(() => {
    if (!active_order || active_order.status !== "running") return;
    const NINETY_MIN = 90 * 60 * 1000;
    refillIntervalRef.current = setInterval(() => {
      triggerRefillAlarm();
    }, NINETY_MIN);
    return () => {
      if (refillIntervalRef.current) clearInterval(refillIntervalRef.current);
      stopRefillSound();
    };
  }, [active_order?.id, active_order?.status, triggerRefillAlarm, stopRefillSound]);

  // Limpar som ao desmontar
  useEffect(() => () => stopRefillSound(), [stopRefillSound]);

  // Alert sound
  const playAlertSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = "square";
      gain.gain.value = 0.3;
      osc.start();
      setTimeout(() => { osc.frequency.value = 1100; }, 200);
      setTimeout(() => { osc.frequency.value = 880; }, 400);
      setTimeout(() => { osc.stop(); ctx.close(); }, 600);
    } catch (e) {}
  }, []);

  // Periodic alerts for weight and quality checks
  useEffect(() => {
    if (!active_order || active_order.status !== "running") return;

    // Pesagem obrigatória: estado vem do servidor (intervalo nas Definições)
    let wasOverdue = false;
    const pollWeightCheck = async () => {
      try {
        const r = await axios.get(`${API}/orders/${active_order.id}/weight-check`);
        setWeightCheck(r.data);
        if (r.data.overdue) {
          setWeightAlert(true);
          if (!wasOverdue) { playAlertSound(); setTimeout(() => playAlertSound(), 1000); }
          wasOverdue = true;
        } else { wasOverdue = false; setWeightAlert(false); }
      } catch {}
    };
    pollWeightCheck();
    const weightInterval = setInterval(pollWeightCheck, 60 * 1000);

    // Quality check every 60 min
    const qualityInterval = setInterval(() => {
      setQualityAlert(true);
      playAlertSound();
      setTimeout(() => playAlertSound(), 800);
      setTimeout(() => playAlertSound(), 1600);
    }, 60 * 60 * 1000);

    // Quality after 30 min
    const firstQuality = setTimeout(() => { setQualityAlert(true); playAlertSound(); setTimeout(() => playAlertSound(), 800); }, 30 * 60 * 1000);

    // Production registration reminder - persistent toast every 60 min
    const showProductionReminder = () => {
      playAlertSound();
      setTimeout(() => playAlertSound(), 600);
      toast(`Hora de registar producao - Ordem ${active_order?.code || ""}`, {
        description: "Carregue no botao verde 'Produção' para registar peças boas e rejeições.",
        duration: 30000,
        icon: "⏰",
        style: { background: "#fef3c7", borderColor: "#f59e0b", color: "#78350f", fontWeight: "bold" },
        id: "production-reminder",
      });
    };
    const productionReminderInterval = setInterval(showProductionReminder, 60 * 60 * 1000);
    const firstProductionReminder = setTimeout(showProductionReminder, 60 * 60 * 1000);

    return () => {
      clearInterval(weightInterval);
      clearInterval(qualityInterval);
      clearTimeout(firstQuality);
      clearInterval(productionReminderInterval);
      clearTimeout(firstProductionReminder);
    };
  }, [active_order?.id, active_order?.status, active_order?.code, playAlertSound]);

  useEffect(() => {
    axios.get(`${API}/stop-reasons`).then(r => setStopReasons(r.data)).catch(() => {});
  }, []);

  const [mpBalance, setMpBalance] = useState(null);
  useEffect(() => {
    if (!active_order?.id) { setMpBalance(null); return; }
    axios.get(`${API}/material-balance/${active_order.id}`).then(r => setMpBalance(r.data)).catch(() => setMpBalance(null));
  }, [active_order?.id, active_order?.current_good_quantity, active_order?.current_rejected_quantity]);

  // KPI calculations
  const goodQtyTotal = active_order?.current_good_quantity || 0;
  const rejQtyTotal = active_order?.current_rejected_quantity || 0;
  const totalProduced = goodQtyTotal + rejQtyTotal;
  const target = active_order?.good_quantity_target || 0;
  const qualityRate = totalProduced > 0 ? ((goodQtyTotal / totalProduced) * 100) : 100;
  const completionRate = target > 0 ? ((goodQtyTotal / target) * 100) : 0;

  const cycleTime = mold?.cycle_time || 0;
  const cavities = mold?.cavities || 1;
  const theoreticalRate = cycleTime > 0 ? (3600 / cycleTime * cavities) : 0;

  // Calculate elapsed time and actual rate
  const startTime = active_order?.started_at ? new Date(active_order.started_at) : null;
  const elapsedHours = startTime ? (Date.now() - startTime.getTime()) / 3600000 : 0;
  const totalStopMinutes = (stops || []).reduce((s, st) => s + (st.duration_minutes || 0), 0);
  const effectiveHours = Math.max(0, elapsedHours - totalStopMinutes / 60);
  const actualRate = effectiveHours > 0 ? totalProduced / effectiveHours : 0;
  const performance = theoreticalRate > 0 ? (actualRate / theoreticalRate) * 100 : 0;
  const availability = elapsedHours > 0 ? (effectiveHours / elapsedHours) * 100 : 100;
  const oee = (availability / 100) * (performance / 100) * (qualityRate / 100) * 100;

  const handleRegisterProduction = async () => {
    if (goodQty === 0 && rejectedQty === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/production-records`, {
        order_id: active_order.id,
        good_quantity: goodQty,
        rejected_quantity: rejectedQty,
        defect_type: defectType || null,
        notes: "",
      });
      toast.success(`Registo: ${goodQty} boas + ${rejectedQty} rejeitadas`);
      setShowProductionDialog(false);
      setGoodQty(0); setRejectedQty(0); setDefectType("");
      onRefresh();
    } catch (e) {
      showError(e, "Erro ao registar");
    } finally { setSubmitting(false); }
  };

  const handleStartStop = async () => {
    if (!stopReason) { toast.error("Selecione um motivo"); return; }
    if (!active_order?.id) { toast.error("Nenhuma ordem activa"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/stop-records/start`, {
        order_id: active_order.id,
        reason: stopReason,
        observations: stopNotes,
      });
      toast.success("Paragem registada");
      setShowStopDialog(false);
      setStopReason(""); setStopNotes("");
      onRefresh();
    } catch (e) {
      showError(e, "Erro ao registar paragem");
    } finally { setSubmitting(false); }
  };

  const handleEndStop = async (stopId) => {
    try {
      await axios.post(`${API}/stop-records/end`, { stop_id: stopId });
      toast.success("Paragem terminada");
      onRefresh();
    } catch (e) { showError(e, "Erro ao terminar paragem"); }
  };

  const handleManualStop = async () => {
    if (!manualStopReason) { toast.error("Selecione um motivo"); return; }
    if (!active_order?.id) { toast.error("Nenhuma ordem activa"); return; }
    if (!manualStopStart || !manualStopEnd) { toast.error("Indique hora inicio e fim"); return; }
    const start = new Date(manualStopStart);
    const end = new Date(manualStopEnd);
    if (end <= start) { toast.error("Hora fim tem de ser posterior a hora de inicio"); return; }
    if (start > new Date() || end > new Date()) { toast.error("As datas nao podem estar no futuro"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/stop-records/manual`, {
        order_id: active_order.id,
        started_at: start.toISOString(),
        ended_at: end.toISOString(),
        reason: manualStopReason,
        observations: manualStopNotes,
      });
      const minutes = Math.round((end - start) / 60000);
      toast.success(`Paragem retroactiva registada (${minutes} min)`);
      setShowManualStopDialog(false);
      setManualStopReason(""); setManualStopNotes("");
      setManualStopStart(""); setManualStopEnd("");
      onRefresh();
    } catch (e) {
      showError(e, "Erro ao registar paragem manual");
    } finally { setSubmitting(false); }
  };

  const handleStartOrder = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/start`);
      toast.success("Ordem iniciada");
      onRefresh();
    } catch (e) { showError(e, "Erro ao iniciar ordem"); }
  };

  const handlePauseOrder = async () => {
    if (!active_order) return;
    try {
      await axios.post(`${API}/orders/${active_order.id}/pause`);
      // Registar como paragem de fábrica (não conta como paragem de máquina)
      toast.success("Ordem em pausa — Fábrica encerrada. Não conta como paragem de máquina.");
      onRefresh();
    } catch (e) { showError(e, "Erro ao pausar ordem"); }
  };

  const handleResumeOrder = async () => {
    if (!active_order) return;
    try {
      await axios.post(`${API}/orders/${active_order.id}/start`);
      toast.success("Ordem retomada");
      onRefresh();
    } catch (e) { showError(e, "Erro ao retomar ordem"); }
  };

  const handleCompleteOrder = async () => {
    if (!active_order) return;
    if (!window.confirm("Encerrar esta ordem?")) return;
    try {
      await axios.post(`${API}/orders/${active_order.id}/complete`);
      toast.success("Ordem concluída");
      onRefresh();
    } catch (e) { showError(e, "Erro ao encerrar ordem"); }
  };

  const activeStop = (stops || []).find(s => !s.ended_at);

  return (
    <div className="h-full flex flex-col" data-testid="op-machine-detail">
      {/* ALARME REABASTECIMENTO MATÉRIA-PRIMA */}
      {refillAlarm && (
        <div className="fixed inset-0 z-[100] bg-red-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className={`bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border-8 ${refillMuted ? "border-amber-500" : "border-red-500 animate-pulse"}`}>
            <div className={`${refillMuted ? "bg-amber-500" : "bg-red-600"} text-white p-6 text-center`}>
              <div className="text-7xl mb-2">🚨</div>
              <div className="text-3xl font-black uppercase tracking-wider">Abastecer Matéria-Prima</div>
              <div className="text-sm mt-2 opacity-90">Máquina {machine.code} · Passaram 1h30 do último aviso</div>
            </div>
            <div className="p-6 text-center">
              <p className="text-lg text-slate-700 mb-6 font-semibold">
                Verifique o nível de matéria-prima na tremonha da máquina e reabasteça se necessário.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={silenceRefillAlarm}
                  disabled={refillMuted}
                  variant="outline"
                  className="h-16 text-base font-bold border-2 border-amber-500 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                  data-testid="op-refill-silence"
                >
                  🔕 Silenciar
                  <span className="block text-[10px] font-normal">1 min</span>
                </Button>
                <Button
                  onClick={confirmRefillDone}
                  className="h-16 text-base font-black bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg"
                  data-testid="op-refill-confirm"
                >
                  ✓ Já Abasteci
                  <span className="block text-[10px] font-normal opacity-90">reinicia 1h30</span>
                </Button>
              </div>
              {refillMuted && (
                <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded p-2">
                  ⚠ Silenciado - o som volta em 1 minuto se não confirmar.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div className={`${statusCfg.barColor} text-white text-center py-2 font-bold text-lg uppercase tracking-wider`}>
        {machine.code} — {activeStop ? `PARAGEM: ${activeStop.reason}` : statusCfg.label}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Action Buttons (Tablet-friendly modern design) */}
        <div className="w-32 bg-gradient-to-b from-slate-100 to-slate-200 border-r border-slate-300 p-2 flex flex-col gap-2 shrink-0 overflow-y-auto shadow-inner">
          <Button className="bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0" onClick={async () => {
            if (!selectedOrderId) { toast.error("Selecione uma ordem na tabela primeiro"); return; }
            try {
              await axios.post(`${API}/orders/${selectedOrderId}/start`);
              toast.success("Ordem iniciada!");
              setSelectedOrderId(null);
              onRefresh();
            } catch (e) { showError(e, "Erro ao iniciar ordem"); }
          }} data-testid="op-btn-execucao">
            <Play className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Execução</span>
          </Button>
          <Button className="bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={() => { if(active_order?.status === "running") setShowProductionDialog(true); }} disabled={active_order?.status !== "running"} data-testid="op-btn-producao">
            <Plus className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Produção</span>
          </Button>
          <Button className="bg-gradient-to-br from-purple-500 to-fuchsia-600 hover:from-purple-600 hover:to-fuchsia-700 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0" onClick={() => { setShowPurgaDialog(true); }} data-testid="op-btn-purga">
            <Droplets className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Purga</span>
          </Button>
          <Button className={`${weightAlert ? "bg-gradient-to-br from-yellow-400 to-amber-500 animate-pulse" : "bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"} text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0`} onClick={() => { setShowWeightDialog(true); setWeightAlert(false); }} data-testid="op-btn-peso">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v17M5.5 8.5l13-1M2 20h20M7.5 8l-2 12M16.5 7l2 13"/></svg>
            <span className="text-[10px] leading-tight">Peso</span>
          </Button>
          <Button className={`${qualityAlert ? "bg-gradient-to-br from-yellow-400 to-amber-500 animate-pulse" : "bg-gradient-to-br from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"} text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0`} onClick={() => { setShowQualityDialog(true); setQualityAlert(false); }} data-testid="op-btn-qualidade">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Qualidade</span>
          </Button>
          <Button className="bg-gradient-to-br from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={() => { setShowRejectDialog(true); }} disabled={active_order?.status !== "running"} data-testid="op-btn-rejeicao">
            <XCircle className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Rejeição</span>
          </Button>
          <Button className="bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0" onClick={() => { setShowSwapDialog(true); }} data-testid="op-btn-troca">
            <ArrowLeftRight className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Troca OP</span>
          </Button>
          <Button className="bg-gradient-to-br from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0" onClick={() => { setShowTecQualidadeDialog(true); }} data-testid="op-btn-tec-qualidade">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></svg>
            <span className="text-[10px] leading-tight">Téc. Qual.</span>
          </Button>
          <Button className="bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={() => { setShowShiftWeightDialog(true); setShiftWeight1(""); setShiftWeight10(""); setShiftWeightNotes(""); }} disabled={!active_order?.id} data-testid="op-btn-peso-turno">
            <Scale className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Peso Turno</span>
          </Button>
          <Button className="bg-gradient-to-br from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={async () => {
            if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
            try {
              const res = await axios.get(`${API}/orders/${active_order.id}/packing-criteria`);
              openPackingSlip(res.data);
            } catch (e) { showError(e, "Erro ao abrir folha de embalagem"); }
          }} disabled={!active_order?.id} data-testid="op-btn-folha-embalagem">
            <Package className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Folha Emb.</span>
          </Button>
          <Button className="bg-gradient-to-br from-slate-700 to-slate-900 hover:from-slate-800 hover:to-black text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={() => {
            if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
            // Pre-calcular sugestao com base na peca
            const upb = part?.units_per_bag || 250;
            const bpb = part?.bags_per_box || 12;
            const defaultQty = upb * bpb;
            const upg = part?.unit_weight || 0;
            const ebw = part?.empty_bag_weight || 15;
            const defaultWeightG = defaultQty * upg + bpb * ebw;
            setBoxQty(String(defaultQty));
            setBoxWeight((defaultWeightG / 1000).toFixed(3));
            setBoxNotes("");
            setShowBoxLabelDialog(true);
          }} disabled={!active_order?.id} data-testid="op-btn-etiqueta-caixa">
            <QrCode className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Etiq. Caixa</span>
          </Button>
          <Button className="bg-gradient-to-br from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={async () => {
            if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
            try {
              const res = await axios.get(`${API}/startup-checklist/template`);
              setClPreItems(res.data.pre_startup_items || []);
              setClSampleItems(res.data.first_sample_items || []);
              setClWeight10("");
              setClApprovedBy("");
              setClNotes("");
              setShowChecklistDialog(true);
            } catch (e) { showError(e, "Erro ao abrir checklist"); }
          }} disabled={!active_order?.id} data-testid="op-btn-checklist-arranque">
            <CheckSquare className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Arranque</span>
          </Button>
          <Button className="bg-gradient-to-br from-blue-700 to-indigo-800 hover:from-blue-800 hover:to-indigo-900 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={async () => {
            if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
            try {
              const shift = encodeURIComponent(window.localStorage.getItem("user_shift") || "morning");
              const today = new Date().toISOString().slice(0, 10);
              const res = await axios.get(`${API}/orders/${active_order.id}/shift-report?shift=${shift}&date=${today}`);
              openShiftReport(res.data);
            } catch (e) { showError(e, "Erro ao gerar relatorio"); }
          }} disabled={!active_order?.id} data-testid="op-btn-relatorio-turno">
            <FileText className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Rel. Turno</span>
          </Button>
          <Button className="bg-gradient-to-br from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0" onClick={() => { setShowManutDialog(true); }} data-testid="op-btn-manut">
            <Wrench className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Manutenção</span>
          </Button>
          <div className="border-t border-slate-300 my-1" />
          <Button className="bg-gradient-to-br from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={() => { setShowStopDialog(true); }} disabled={!!activeStop} data-testid="op-btn-paragem">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Paragem</span>
          </Button>
          {activeStop && (
            <Button className="bg-gradient-to-br from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 ring-2 ring-green-300 animate-pulse" onClick={() => handleEndStop(activeStop.id)} data-testid="op-btn-fim-paragem">
              <Play className="h-5 w-5" />
              <span className="text-[10px] leading-tight">Fim Paragem</span>
            </Button>
          )}
          <Button
            className="bg-gradient-to-br from-amber-700 to-orange-800 hover:from-amber-800 hover:to-orange-900 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400"
            onClick={() => {
              const now = new Date();
              const localISO = (d) => {
                const off = d.getTimezoneOffset();
                const local = new Date(d.getTime() - off * 60000);
                return local.toISOString().slice(0, 16);
              };
              setManualStopEnd(localISO(now));
              setManualStopStart(localISO(new Date(now.getTime() - 15 * 60000)));
              setManualStopReason("");
              setManualStopNotes("");
              setShowManualStopDialog(true);
            }}
            disabled={!active_order}
            data-testid="op-btn-paragem-manual"
            title="Registar paragem que ja ocorreu"
          >
            <AlertTriangle className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Parag. Manual</span>
          </Button>
          <Button className="bg-gradient-to-br from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={active_order?.status === "running" ? handlePauseOrder : handleResumeOrder} disabled={!active_order || (active_order?.status !== "running" && active_order?.status !== "paused")} data-testid="op-btn-pausa-op">
            <Pause className="h-5 w-5" />
            <span className="text-[10px] leading-tight">{active_order?.status === "paused" ? "Retomar OP" : "Fim Turno OP"}</span>
          </Button>
          <Button className="bg-gradient-to-br from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white text-xs h-14 flex-col gap-1 rounded-xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all border-0 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-400" onClick={handleCompleteOrder} disabled={!active_order} data-testid="op-btn-encerrar">
            <Square className="h-5 w-5" />
            <span className="text-[10px] leading-tight">Encerrar OP</span>
          </Button>
          <div className="flex-1 min-h-2" />
          <Button variant="ghost" className="text-xs h-12 flex-col gap-0.5 text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 font-semibold rounded-xl" onClick={onBack} data-testid="op-btn-voltar">
            <ChevronLeft className="h-4 w-4" />
            <span className="text-[10px]">Página prin.</span>
          </Button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
          {/* Visual Alerts */}
          {weightAlert && (
            <div className="bg-yellow-400 text-yellow-900 p-3 rounded-lg flex items-center justify-between animate-pulse shadow-lg" data-testid="weight-alert">
              <div className="flex items-center gap-3">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v17M5.5 8.5l13-1M2 20h20M7.5 8l-2 12M16.5 7l2 13"/></svg>
                <div>
                  <p className="font-black text-sm">{weightCheck?.blocked ? "PESAGEM EM FALTA — REGISTO DE PRODUÇÃO BLOQUEADO" : "CONTROLO DE PESO NECESSÁRIO"}</p>
                  <p className="text-xs">{weightCheck?.never_weighed ? "Ordem ainda sem pesagem" : `Última pesagem há ${weightCheck?.minutes_since ?? "?"} min`} · limite {weightCheck?.interval_min ?? 60} min. Peso esperado: {part?.unit_weight || "?"} g (±{part?.weight_tolerance_pct || 5}%)</p>
                </div>
              </div>
              <Button className="bg-yellow-900 hover:bg-yellow-800 text-yellow-100" onClick={() => { setShowWeightDialog(true); }} data-testid="weight-alert-register-btn">Registar Peso</Button>
            </div>
          )}
          {qualityAlert && (
            <div className="bg-orange-400 text-orange-900 p-3 rounded-lg flex items-center justify-between animate-pulse shadow-lg" data-testid="quality-alert">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6" />
                <div>
                  <p className="font-black text-sm">CONTROLO DE QUALIDADE NECESSÁRIO</p>
                  <p className="text-xs">Faça inspeção visual/dimensional à amostra e registe o resultado.</p>
                </div>
              </div>
              <Button className="bg-orange-900 hover:bg-orange-800 text-orange-100" onClick={() => { setShowQualityDialog(true); setQualityAlert(false); }}>Registar Qualidade</Button>
            </div>
          )}

          {/* KPIs - Tablet friendly: cartoes grandes com gradientes e barra de progresso */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "OEE", value: oee, target: 60, midTarget: 40, icon: "📊" },
              { label: "Disponibilidade", value: availability, target: 85, midTarget: 70, icon: "⏱️" },
              { label: "Desempenho", value: performance, target: 85, midTarget: 70, icon: "⚡" },
              { label: "Qualidade", value: qualityRate, target: 95, midTarget: 85, icon: "✓" },
            ].map((kpi, i) => {
              const v = kpi.value;
              const colorClass = v >= kpi.target ? "from-emerald-500 to-green-600" : v >= kpi.midTarget ? "from-amber-400 to-orange-500" : "from-red-500 to-rose-600";
              const textColor = v >= kpi.target ? "text-emerald-700" : v >= kpi.midTarget ? "text-amber-700" : "text-red-700";
              return (
                <div key={i} className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow border-0 overflow-hidden">
                  <div className={`bg-gradient-to-br ${colorClass} px-4 py-3 text-white`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider opacity-90">{kpi.label}</span>
                      <span className="text-xl">{kpi.icon}</span>
                    </div>
                  </div>
                  <div className="px-4 py-5 text-center">
                    <p className={`text-5xl font-extrabold font-mono ${textColor} leading-none`}>
                      {v.toFixed(1)}<span className="text-2xl ml-1">%</span>
                    </p>
                    <div className="mt-3 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className={`bg-gradient-to-r ${colorClass} h-full rounded-full transition-all`} style={{ width: `${Math.min(100, v)}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 uppercase tracking-wide">
                      {v >= kpi.target ? "Bom" : v >= kpi.midTarget ? "Atenção" : "Crítico"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Availability Timeline - acima da lista de ordens */}
          <AvailabilityTimeline stops={stops || []} startedAt={active_order?.started_at} />

          {/* Dashboard de Producao da Maquina - acima da lista de ordens */}
          <div className="space-y-4 border-t-2 border-blue-200 pt-4">
            {/* Informacao do Operador */}
            <div className="bg-white border rounded-lg p-4">
              <p className="text-red-600 font-bold text-sm mb-2">Informação do Operador</p>
              <div className="flex items-center gap-4">
                <OperatorAvatar
                  photo={operator?.photo_url || user?.photo_url}
                  name={operator?.name || user?.name}
                  code={operator?.user_code || user?.user_code}
                  size="lg"
                />
                <div className="grid grid-cols-4 gap-4 text-sm flex-1">
                  <div><p className="text-xs text-slate-400">Nome</p><p className="font-bold">{operator?.name || user?.name || "-"}</p></div>
                  <div><p className="text-xs text-slate-400">Código</p><p className="font-bold font-mono">{operator?.user_code || user?.user_code || "-"}</p></div>
                  <div><p className="text-xs text-slate-400">Função</p><p className="font-bold">{operator?.function || (user?.role === "admin" ? "Administrador" : user?.role === "operator" ? "Operador" : user?.role === "quality" ? "Qualidade" : user?.role === "shift_leader" ? "Chefe de Turno" : "-")}</p></div>
                  <div><p className="text-xs text-slate-400">Tempo no Turno</p><p className="font-bold">{(() => { const h = Math.floor(elapsedHours); const m = Math.floor((elapsedHours - h) * 60); return `${h}h ${m}min`; })()}</p></div>
                </div>
              </div>
            </div>

            {/* Packaging Progress + Shift Notes */}
            {active_order?.id && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PackagingProgress orderId={active_order.id} />
                <div className="bg-white border rounded-lg">
                  <ShiftNotes orderId={active_order.id} machineId={machine?.id} compact={false} />
                </div>
              </div>
            )}

            {/* Producao */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-white border rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 uppercase font-semibold">Planeado</p>
                <p className="text-2xl font-bold mt-1">{(target || 0).toLocaleString()}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-xs text-green-600 uppercase font-semibold">Produção Boa</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{goodQtyTotal.toLocaleString()}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-xs text-red-600 uppercase font-semibold">Rejeitado</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{rejQtyTotal.toLocaleString()}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-xs text-amber-600 uppercase font-semibold">Taxa Rejeição</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{totalProduced > 0 ? ((rejQtyTotal / totalProduced) * 100).toFixed(2) : "0"}%</p>
              </div>
            </div>

            {/* Tempos e MP */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-white border rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 uppercase font-semibold">Tempo Produtivo</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{Math.floor(effectiveHours)}h {Math.floor((effectiveHours % 1) * 60)}min</p>
              </div>
              <div className="bg-white border rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 uppercase font-semibold">Tempo Parado</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{Math.floor(totalStopMinutes / 60)}h {Math.round(totalStopMinutes % 60)}min</p>
              </div>
              <div className="bg-white border rounded-lg p-4 text-center" data-testid="op-mp-planned">
                <p className="text-xs text-slate-400 uppercase font-semibold">MP Prevista (kg)</p>
                <p className="text-2xl font-bold mt-1">{mpBalance ? mpBalance.planned_kg.toFixed(2) : "-"}</p>
                <p className="text-[10px] text-slate-400">{mpBalance?.unit_weight_theoretical_g ? `${mpBalance.unit_weight_theoretical_g} g/peça teórico` : ""}</p>
              </div>
              <div className="bg-white border rounded-lg p-4 text-center" data-testid="op-mp-real">
                <p className="text-xs text-slate-400 uppercase font-semibold">MP Real Gasta (kg)</p>
                <p className={`text-2xl font-bold mt-1 ${mpBalance && Math.abs(mpBalance.deviation_pct) >= 8 ? "text-red-600" : ""}`}>{mpBalance ? mpBalance.real_kg.toFixed(2) : "-"}</p>
                <p className={`text-[10px] ${mpBalance?.unit_weight_measured_g ? "text-blue-600 font-semibold" : "text-slate-400"}`}>
                  {mpBalance?.unit_weight_measured_g ? `⚖ ${mpBalance.unit_weight_measured_g} g/peça (operador)` : "sem pesagem — peso teórico"}
                  {mpBalance && mpBalance.pieces_produced > 0 ? ` · ${mpBalance.deviation_pct > 0 ? "+" : ""}${mpBalance.deviation_pct}%` : ""}
                </p>
              </div>
            </div>
          </div>

          {/* Active Orders - Tablet friendly card layout */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-blue-600" />
                Lista de Ordens Activas
              </h3>
              <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                {(all_orders || []).length} ordem(ns)
              </span>
            </div>
            {(!all_orders || all_orders.length === 0) ? (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400">
                <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-40" />
                <p>Nenhuma ordem atribuída a esta máquina</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(all_orders || []).map((o, i) => {
                  const isActive = o.status === "running";
                  const isPaused = o.status === "paused";
                  const isSelected = selectedOrderId === o.id;
                  const planned = o.good_quantity_target || 0;
                  const done = o.current_good_quantity || 0;
                  const progress = planned > 0 ? Math.min(100, (done / planned) * 100) : 0;
                  
                  const statusMeta = isActive ? { label: "EM PRODUÇÃO", bg: "bg-emerald-500", text: "text-white", ring: "ring-emerald-400" }
                    : isPaused ? { label: "EM PARAGEM", bg: "bg-amber-500", text: "text-white", ring: "ring-amber-400" }
                    : o.status === "completed" ? { label: "CONCLUÍDA", bg: "bg-blue-500", text: "text-white", ring: "ring-blue-400" }
                    : o.status === "planned" ? { label: "PLANEADA", bg: "bg-slate-300", text: "text-slate-800", ring: "ring-slate-300" }
                    : { label: "PENDENTE", bg: "bg-slate-200", text: "text-slate-700", ring: "ring-slate-300" };
                  
                  return (
                    <button
                      key={o.id || i}
                      onClick={() => setSelectedOrderId(o.id)}
                      className={`text-left bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all overflow-hidden border-2 ${isSelected ? "border-amber-400 ring-4 ring-amber-100" : isActive ? "border-emerald-300" : "border-slate-200 hover:border-blue-300"}`}
                      data-testid={`order-row-${o.code}`}
                    >
                      {/* Header: Codigo + Status badge */}
                      <div className={`px-4 py-2 flex items-center justify-between ${isActive ? "bg-emerald-50" : isPaused ? "bg-amber-50" : "bg-slate-50"}`}>
                        <span className="font-mono font-extrabold text-slate-900 text-base tracking-tight">{o.code}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${statusMeta.bg} ${statusMeta.text}`}>
                          {statusMeta.label}
                        </span>
                      </div>
                      
                      {/* Body */}
                      <div className="px-4 py-3 space-y-2">
                        {/* Peca + descricao */}
                        <div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{o.mold_code || "-"}</span>
                            <span>·</span>
                            <span className="font-mono">{o.part_code || "-"}</span>
                            {o.lot_number && <><span>·</span><span className="font-mono text-blue-600">Lote {o.lot_number}</span></>}
                          </div>
                          <p className="font-semibold text-slate-800 truncate mt-0.5">{o.part_description || "-"}</p>
                        </div>
                        
                        {/* Progresso */}
                        <div>
                          <div className="flex items-baseline justify-between mb-1">
                            <span className="text-xs text-slate-500">Progresso</span>
                            <span className="text-sm font-bold text-slate-800 font-mono">
                              {done.toLocaleString()} / {planned.toLocaleString()}
                              <span className="text-xs text-slate-500 ml-1">UN</span>
                            </span>
                          </div>
                          <div className="bg-slate-100 rounded-full h-2.5 overflow-hidden">
                            <div className={`h-full transition-all ${isActive ? "bg-gradient-to-r from-emerald-400 to-green-600" : isPaused ? "bg-gradient-to-r from-amber-400 to-orange-500" : "bg-gradient-to-r from-slate-300 to-slate-400"}`} style={{ width: `${progress}%` }} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5 text-right font-mono">{progress.toFixed(1)}%</p>
                        </div>
                        
                        {/* Datas */}
                        <div className="grid grid-cols-2 gap-2 text-[11px] pt-1">
                          <div>
                            <p className="text-slate-400 uppercase font-semibold">Início Real</p>
                            <p className="text-slate-700 font-mono">{o.started_at ? new Date(o.started_at).toLocaleString("pt-PT", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—"}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 uppercase font-semibold">Fim Estimado</p>
                            <p className="text-slate-700 font-mono">{o.estimated_end_time ? new Date(o.estimated_end_time).toLocaleString("pt-PT", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—"}</p>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Production Dialog */}
      <Dialog open={showProductionDialog} onOpenChange={setShowProductionDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registar Produção</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Peças Boas</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setGoodQty(Math.max(0, goodQty - 1))}><Minus className="h-4 w-4" /></Button>
                  <Input type="number" min="0" value={goodQty} onChange={e => setGoodQty(parseInt(e.target.value) || 0)} className="text-center font-mono text-lg" />
                  <Button variant="outline" size="icon" onClick={() => setGoodQty(goodQty + 1)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Peças Rejeitadas</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setRejectedQty(Math.max(0, rejectedQty - 1))}><Minus className="h-4 w-4" /></Button>
                  <Input type="number" min="0" value={rejectedQty} onChange={e => setRejectedQty(parseInt(e.target.value) || 0)} className="text-center font-mono text-lg" />
                  <Button variant="outline" size="icon" onClick={() => setRejectedQty(rejectedQty + 1)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
            {rejectedQty > 0 && (
              <div className="space-y-2">
                <Label>Tipo de Defeito</Label>
                <Input value={defectType} onChange={e => setDefectType(e.target.value)} placeholder="Ex: Rebarbas, Deformação..." />
              </div>
            )}
            <Button className="w-full" onClick={handleRegisterProduction} disabled={submitting || (goodQty === 0 && rejectedQty === 0)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirmar Registo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stop Dialog */}
      <Dialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-orange-600"><AlertTriangle className="h-5 w-5" /> Registar Paragem</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="font-bold">Tipo de Paragem <span className="text-red-500">*</span></Label>
              
              <p className="text-xs font-semibold text-slate-500 mt-2 mb-1">PARAGENS PLANEADAS</p>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  "Troca de Molde (Setup)",
                  "Manutenção Preventiva",
                  "Limpeza e Purga",
                  "Testes de Moldes (Try-out)",
                ].map(r => (
                  <button key={r} type="button" onClick={() => setStopReason(r)}
                    className={`text-left text-xs px-3 py-2 rounded-md border transition-all ${stopReason === r ? "bg-blue-100 border-blue-500 text-blue-800 font-bold" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {stopReason === r ? "✓ " : ""}{r}
                  </button>
                ))}
              </div>

              <p className="text-xs font-semibold text-red-500 mt-3 mb-1">PARAGENS NÃO PLANEADAS</p>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  "Avaria Mecânica/Hidráulica",
                  "Avaria Elétrica/Eletrónica",
                  "Problemas no Molde",
                  "Falta de Matéria-Prima",
                  "Falta de Periféricos",
                  "Qualidade (Peças Defeituosas)",
                ].map(r => (
                  <button key={r} type="button" onClick={() => setStopReason(r)}
                    className={`text-left text-xs px-3 py-2 rounded-md border transition-all ${stopReason === r ? "bg-red-100 border-red-500 text-red-800 font-bold" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {stopReason === r ? "✓ " : ""}{r}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Input value={stopNotes} onChange={e => setStopNotes(e.target.value)} placeholder="Detalhes da paragem..." />
            </div>
            {stopReason && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-xs text-orange-600 font-semibold">Paragem: {stopReason}</p>
                {stopNotes && <p className="text-xs text-orange-500 mt-1">{stopNotes}</p>}
              </div>
            )}
            <Button className="w-full bg-orange-600 hover:bg-orange-700" onClick={handleStartStop} disabled={submitting || !stopReason}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Confirmar Paragem
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual (Retroactive) Stop Dialog */}
      <Dialog open={showManualStopDialog} onOpenChange={setShowManualStopDialog}>
        <DialogContent className="max-w-lg" data-testid="manual-stop-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" /> Registar Paragem Retroactiva
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Use quando se esqueceu de marcar uma paragem que ja ocorreu.</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold">Hora Inicio <span className="text-red-500">*</span></Label>
                <Input
                  type="datetime-local"
                  value={manualStopStart}
                  onChange={e => setManualStopStart(e.target.value)}
                  data-testid="manual-stop-start"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold">Hora Fim <span className="text-red-500">*</span></Label>
                <Input
                  type="datetime-local"
                  value={manualStopEnd}
                  onChange={e => setManualStopEnd(e.target.value)}
                  data-testid="manual-stop-end"
                />
              </div>
            </div>
            {manualStopStart && manualStopEnd && new Date(manualStopEnd) > new Date(manualStopStart) && (
              <div className="text-xs text-slate-600 bg-slate-50 border rounded-md px-3 py-2">
                Duracao: <span className="font-bold">{Math.round((new Date(manualStopEnd) - new Date(manualStopStart)) / 60000)} minutos</span>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-bold">Motivo <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  "Troca de Molde (Setup)",
                  "Manutenção Preventiva",
                  "Limpeza e Purga",
                  "Avaria Mecânica/Hidráulica",
                  "Avaria Elétrica/Eletrónica",
                  "Problemas no Molde",
                  "Falta de Matéria-Prima",
                  "Outro",
                ].map(r => (
                  <button key={r} type="button" onClick={() => setManualStopReason(r)}
                    className={`text-left text-xs px-3 py-2 rounded-md border transition-all ${manualStopReason === r ? "bg-amber-100 border-amber-500 text-amber-900 font-bold" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`manual-stop-reason-${r}`}>
                    {manualStopReason === r ? "✓ " : ""}{r}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Observacoes</Label>
              <Input
                value={manualStopNotes}
                onChange={e => setManualStopNotes(e.target.value)}
                placeholder="Detalhes da paragem..."
                data-testid="manual-stop-notes"
              />
            </div>
            <Button
              className="w-full bg-amber-700 hover:bg-amber-800"
              onClick={handleManualStop}
              disabled={submitting || !manualStopReason || !manualStopStart || !manualStopEnd}
              data-testid="manual-stop-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Registar Paragem
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Purga Dialog */}
      <Dialog open={showPurgaDialog} onOpenChange={setShowPurgaDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registar Purga</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Quantidade de Material Purgado (kg)</Label>
              <Input type="number" step="0.1" min="0" value={purgaKg} onChange={e => setPurgaKg(e.target.value)} placeholder="Ex: 2.5" />
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Input value={purgaNotes} onChange={e => setPurgaNotes(e.target.value)} placeholder="Motivo da purga, cor anterior..." />
            </div>
            <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={async () => {
              if (!purgaKg) { toast.error("Introduza a quantidade"); return; }
              setSubmitting(true);
              try {
                await axios.post(`${API}/stop-records/start`, { order_id: active_order?.id, reason: "Limpeza e Purga", observations: `${purgaKg} kg purgados. ${purgaNotes}` });
                toast.success(`Purga registada: ${purgaKg} kg`);
                setShowPurgaDialog(false); setPurgaKg(""); setPurgaNotes(""); onRefresh();
              } catch (e) { showError(e, "Erro ao processar a operação"); }
              finally { setSubmitting(false); }
            }} disabled={submitting}>
              <Droplets className="h-4 w-4 mr-2" /> Confirmar Purga
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manutenção Dialog */}
      <Dialog open={showManutDialog} onOpenChange={setShowManutDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registar Manutenção</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Descrição da Manutenção</Label>
              <Input value={manutNotes} onChange={e => setManutNotes(e.target.value)} placeholder="O que foi feito..." />
            </div>
            <Button className="w-full bg-pink-600 hover:bg-pink-700" onClick={async () => {
              if (!manutNotes) { toast.error("Descreva a manutenção"); return; }
              setSubmitting(true);
              try {
                await axios.post(`${API}/stop-records/start`, { order_id: active_order?.id, reason: "Manutenção Preventiva", observations: manutNotes });
                toast.success("Manutenção registada");
                setShowManutDialog(false); setManutNotes(""); onRefresh();
              } catch (e) { showError(e, "Erro ao processar a operação"); }
              finally { setSubmitting(false); }
            }} disabled={submitting}>
              <Wrench className="h-4 w-4 mr-2" /> Confirmar Manutenção
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Troca OP Dialog */}
      <Dialog open={showSwapDialog} onOpenChange={setShowSwapDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Trocar Ordem de Produção</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">Selecione a próxima ordem para iniciar nesta máquina:</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {(all_orders || []).filter(o => o.status !== "running" && o.status !== "completed" && o.status !== "cancelled").map(o => (
                <div key={o.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                  <div>
                    <p className="font-mono font-bold">{o.code}</p>
                    <p className="text-xs text-muted-foreground">{o.part_code} - {o.part_description}</p>
                  </div>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                    try {
                      if (active_order?.status === "running") {
                        await axios.post(`${API}/orders/${active_order.id}/pause`);
                      }
                      await axios.post(`${API}/orders/${o.id}/start`);
                      toast.success(`Trocado para ${o.code}`);
                      setShowSwapDialog(false); onRefresh();
                    } catch (e) { showError(e, "Erro"); }
                  }}>
                    <Play className="h-3 w-3 mr-1" /> Iniciar
                  </Button>
                </div>
              ))}
              {(all_orders || []).filter(o => o.status !== "running" && o.status !== "completed" && o.status !== "cancelled").length === 0 && (
                <p className="text-center text-muted-foreground py-4">Nenhuma ordem disponível para troca</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <WeightHistoryDialog open={showWeightHistory} onOpenChange={setShowWeightHistory} orderId={active_order?.id} orderCode={active_order?.code} />
      {/* Weight Dialog */}
      <Dialog open={showWeightDialog} onOpenChange={setShowWeightDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span>Controlo de Peso + Tempo de Ciclo</span>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowWeightHistory(true)} data-testid="op-weight-history-btn">
                <History className="h-3.5 w-3.5 mr-1" /> Histórico
              </Button>
            </DialogTitle>
          </DialogHeader>
          {weightCheck && (
            <div className={`text-xs rounded px-3 py-1.5 flex items-center justify-between ${weightCheck.overdue ? "bg-red-100 text-red-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="weight-check-chip">
              <span>{weightCheck.never_weighed ? "Ordem ainda sem pesagem" : `Última pesagem há ${weightCheck.minutes_since} min`}</span>
              <span className="font-semibold">{weightCheck.overdue ? "EM ATRASO" : `próxima em ${weightCheck.due_in_min} min`}</span>
            </div>
          )}
          <div className="space-y-4 mt-2">
            {(() => {
              const tol = (part?.weight_tolerance_pct || 5) / 100;
              const cav = mold?.cavities || 1;
              const unitNom = part?.unit_weight || 0;
              const unitMin = part?.weight_min || (unitNom ? unitNom * (1 - tol) : 0);
              const unitMax = part?.weight_max || (unitNom ? unitNom * (1 + tol) : 0);
              const shotNom = active_order?.weight_per_cycle || (unitNom ? unitNom * cav + (mold?.runner_weight || 0) : 0);
              const shotMin = shotNom * (1 - tol), shotMax = shotNom * (1 + tol);
              const unitDev = weightValue && unitNom ? ((parseFloat(weightValue) - unitNom) / unitNom) * 100 : null;
              const shotDev = shotWeightValue && shotNom ? ((parseFloat(shotWeightValue) - shotNom) / shotNom) * 100 : null;
              const renderRow = ({ label, nom, min, max, value, setValue, setResult, result, dev, testId, hint }) => (
                <div className="border rounded-lg p-3 space-y-2" data-testid={`${testId}-block`}>
                  <div className="flex items-center justify-between">
                    <Label className="font-bold">{label}</Label>
                    <span className="text-xs text-muted-foreground">{hint}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-sm bg-slate-50 rounded p-2">
                    <div><p className="text-[10px] text-muted-foreground uppercase">Nominal</p><p className="font-bold" data-testid={`${testId}-nominal`}>{nom ? nom.toFixed(2) : "?"} g</p></div>
                    <div><p className="text-[10px] text-muted-foreground uppercase">Mínimo</p><p className="font-bold text-red-600">{min ? min.toFixed(2) : "?"} g</p></div>
                    <div><p className="text-[10px] text-muted-foreground uppercase">Máximo</p><p className="font-bold text-red-600">{max ? max.toFixed(2) : "?"} g</p></div>
                  </div>
                  <Input type="number" step="0.01" min="0" value={value} onChange={e => {
                    setValue(e.target.value);
                    const v = parseFloat(e.target.value);
                    if (!v || !nom) { setResult(null); return; }
                    setResult(v >= min && v <= max ? "ok" : "nok");
                  }} placeholder={nom ? `Ex: ${nom.toFixed(1)}` : "g"} className="text-center font-mono text-xl h-14" data-testid={`${testId}-input`} />
                  {result && (
                    <div className={`p-2 rounded-lg text-center font-bold ${result === "ok" ? "bg-green-100 text-green-800 border border-green-500" : "bg-red-100 text-red-800 border border-red-500"}`} data-testid={`${testId}-result`}>
                      {result === "ok" ? "APROVADO" : "REJEITADO — fora dos limites"}
                      {dev !== null && <span className="text-sm font-normal ml-2">({dev > 0 ? "+" : ""}{dev.toFixed(1)}%{result === "nok" ? " · chefe de turno será avisado" : ""})</span>}
                    </div>
                  )}
                </div>
              );
              return (
                <>
                  {renderRow({ label: "Peso de 1 peça (g)", nom: unitNom, min: unitMin, max: unitMax, value: weightValue, setValue: setWeightValue,
                    setResult: setWeightResult, result: weightResult, dev: unitDev, testId: "weight", hint: `tolerância ±${(tol * 100).toFixed(0)}%` })}
                  {renderRow({ label: "Peso da moldação completa com gito (g)", nom: shotNom, min: shotMin, max: shotMax, value: shotWeightValue, setValue: setShotWeightValue,
                    setResult: setShotWeightResult, result: shotWeightResult, dev: shotDev, testId: "shot-weight", hint: `${cav} cav. × ${unitNom || "?"} g + gito ${mold?.runner_weight || 0} g` })}
                </>
              );
            })()}

            {/* Tempo de Ciclo */}
            <div className="border-t-2 border-blue-100 pt-4 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-blue-700 font-bold">Tempo de Ciclo Medido (segundos)</Label>
                {mold?.cycle_time && (
                  <span className="text-xs text-muted-foreground">
                    Teórico: <strong>{mold.cycle_time}s</strong> · {mold.cavities || 1} cav.
                  </span>
                )}
              </div>
              <Input
                type="number" step="0.1" min="0" max="600"
                value={cycleTimeValue}
                onChange={e => setCycleTimeValue(e.target.value)}
                placeholder={mold?.cycle_time ? `Ex: ${mold.cycle_time}` : "Ex: 25.5"}
                className="text-center font-mono text-xl h-14 border-blue-300"
                data-testid="cycle-time-input"
              />
              {cycleTimeValue && mold?.cycle_time && (() => {
                const measured = parseFloat(cycleTimeValue);
                const theo = mold.cycle_time;
                const dev = ((measured - theo) / theo) * 100;
                const isSlower = dev > 0;
                return (
                  <div className={`p-2 rounded text-xs text-center ${Math.abs(dev) < 5 ? "bg-emerald-50 text-emerald-700" : isSlower ? "bg-amber-50 text-amber-700 font-semibold" : "bg-blue-50 text-blue-700"}`}>
                    {Math.abs(dev) < 5
                      ? `✓ Desvio ${dev >= 0 ? "+" : ""}${dev.toFixed(1)}% - dentro do normal`
                      : isSlower
                        ? `⚠ ${dev.toFixed(1)}% mais LENTO que o teórico - vai atrasar o plano`
                        : `↑ ${Math.abs(dev).toFixed(1)}% mais RÁPIDO que o teórico - vai adiantar o plano`}
                  </div>
                );
              })()}
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={updateMoldCycle}
                  onChange={e => setUpdateMoldCycle(e.target.checked)}
                  className="rounded"
                  data-testid="cycle-update-mold"
                />
                Actualizar tempo de ciclo do molde ({mold?.code || "-"}) como novo valor de referência
              </label>
            </div>

            <Button className="w-full" onClick={async () => {
              if (!weightValue && !shotWeightValue) { toast.error("Introduza o peso da peça e/ou da moldação completa"); return; }
              setSubmitting(true);
              try {
                // 1) Registar pesagem (alimenta MP Real e alerta chefe de turno se fora da tolerância)
                const wr = await axios.post(`${API}/weight-records`, {
                  order_id: active_order?.id,
                  weight: weightValue ? parseFloat(weightValue) : null,
                  shot_weight: shotWeightValue ? parseFloat(shotWeightValue) : null,
                  notes: `Controlo Peso${cycleTimeValue ? ` | CT medido: ${cycleTimeValue}s` : ""}`,
                });
                if (wr.data?.weight_status && wr.data.weight_status !== "ok") {
                  toast.warning(`⚖ Peça: desvio ${wr.data.deviation_pct > 0 ? "+" : ""}${wr.data.deviation_pct}% — chefe de turno avisado`, { duration: 8000 });
                } else if (wr.data?.shot_status && wr.data.shot_status !== "ok") {
                  toast.warning(`⚖ Moldação: desvio ${wr.data.shot_deviation_pct > 0 ? "+" : ""}${wr.data.shot_deviation_pct}% — chefe de turno avisado`, { duration: 8000 });
                }
                // 2) Se ciclo foi introduzido, chamar endpoint de recalculo
                if (cycleTimeValue && active_order?.id) {
                  const r = await axios.post(`${API}/orders/${active_order.id}/cycle-time-report`, {
                    cycle_time_seconds: parseFloat(cycleTimeValue),
                    update_mold: updateMoldCycle,
                    notes: `Registado com controlo de peso: ${weightValue || "-"}g / moldação ${shotWeightValue || "-"}g`,
                  });
                  const d = r.data || {};
                  const delta = d.delta_delay_minutes || 0;
                  if (Math.abs(delta) >= 1) {
                    const affected = d.affected_next_orders || 0;
                    if (delta > 0) {
                      toast.warning(`⚠ Plano atrasado em ${delta}min - propagado a ${affected} ordem(ns)`, { duration: 6000 });
                    } else {
                      toast.success(`✓ Plano adiantado ${Math.abs(delta)}min - propagado a ${affected} ordem(ns)`, { duration: 6000 });
                    }
                  } else {
                    toast.success(`✓ Tempo de ciclo registado (${cycleTimeValue}s) - sem impacto no plano`);
                  }
                }
                toast.success(`Pesagem registada${weightValue ? ` · peça ${weightValue}g` : ""}${shotWeightValue ? ` · moldação ${shotWeightValue}g` : ""}`);
                setShowWeightDialog(false);
                setWeightValue(""); setWeightResult(null);
                setShotWeightValue(""); setShotWeightResult(null);
                setCycleTimeValue(""); setUpdateMoldCycle(false);
                setWeightAlert(false);
                axios.get(`${API}/orders/${active_order.id}/weight-check`).then(r => setWeightCheck(r.data)).catch(() => {});
                onRefresh();
              } catch (e) { showError(e, "Erro ao processar a operação"); }
              finally { setSubmitting(false); }
            }} disabled={submitting || (!weightValue && !shotWeightValue)} data-testid="op-weight-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Registar Peso {cycleTimeValue ? "+ Tempo de Ciclo" : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quality Dialog */}
      <Dialog open={showQualityDialog} onOpenChange={setShowQualityDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Controlo de Qualidade</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium">Ordem: <span className="font-mono">{active_order?.code}</span></p>
              <p className="text-muted-foreground">Peça: {part?.code} - {part?.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tamanho da Amostra</Label>
                <Input type="number" min="1" value={qualitySampleSize} onChange={e => setQualitySampleSize(e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label>Peças com Defeito</Label>
                <Input type="number" min="0" value={qualityDefectCount} onChange={e => setQualityDefectCount(e.target.value)} className="font-mono" />
              </div>
            </div>
            {parseInt(qualityDefectCount) > 0 && (
              <div className="space-y-2">
                <Label>Tipos de Defeito</Label>
                <Input value={qualityDefects} onChange={e => setQualityDefects(e.target.value)} placeholder="Ex: Rebarbas, Manchas, Deformação..." />
              </div>
            )}
            <div className="space-y-2">
              <Label>Observações</Label>
              <Input value={qualityNotes} onChange={e => setQualityNotes(e.target.value)} placeholder="Visual OK, Dimensional OK..." />
            </div>
            {/* Result preview */}
            {(() => {
              const sample = parseInt(qualitySampleSize) || 0;
              const defects = parseInt(qualityDefectCount) || 0;
              const rate = sample > 0 ? ((sample - defects) / sample * 100) : 100;
              const passed = defects === 0;
              return (
                <div className={`p-3 rounded-lg text-center font-bold ${passed ? "bg-green-100 text-green-800 border border-green-500" : "bg-red-100 text-red-800 border border-red-500"}`}>
                  {passed ? "APROVADO" : "REJEITADO"} — {rate.toFixed(0)}% conforme ({sample - defects}/{sample})
                </div>
              );
            })()}
            <Button className="w-full" onClick={async () => {
              const sample = parseInt(qualitySampleSize) || 0;
              const defects = parseInt(qualityDefectCount) || 0;
              if (sample === 0) { toast.error("Defina o tamanho da amostra"); return; }
              setSubmitting(true);
              try {
                const rate = ((sample - defects) / sample * 100);
                await axios.post(`${API}/production-records`, {
                  order_id: active_order?.id,
                  good_quantity: 0, rejected_quantity: defects,
                  defect_type: qualityDefects || (defects > 0 ? "Defeito visual" : null),
                  notes: `Controlo Qualidade: ${sample - defects}/${sample} OK (${rate.toFixed(1)}%) | ${defects === 0 ? "APROVADO" : "REJEITADO"} | ${qualityNotes}`,
                });
                toast.success(`Qualidade registada: ${defects === 0 ? "APROVADO" : "REJEITADO"} (${rate.toFixed(0)}%)`);
                setShowQualityDialog(false); setQualitySampleSize("5"); setQualityDefectCount("0"); setQualityDefects(""); setQualityNotes(""); onRefresh();
              } catch (e) { showError(e, "Erro ao processar a operação"); }
              finally { setSubmitting(false); }
            }} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Registar Qualidade
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><XCircle className="h-5 w-5" /> Registar Rejeição</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Quantidade Rejeitada</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setRejectQty(Math.max(1, rejectQty - 1))}><Minus className="h-4 w-4" /></Button>
                <Input type="number" min="1" value={rejectQty} onChange={e => setRejectQty(parseInt(e.target.value) || 1)} className="text-center font-mono text-xl h-12" />
                <Button variant="outline" size="icon" onClick={() => setRejectQty(rejectQty + 1)}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo(s) de Defeito <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-[240px] overflow-y-auto">
                {[
                  "Rebarba", "Falta de material", "Queimado", "Dimensional fora",
                  "Defeito visual", "Chupados (Sink Marks)", "Linhas de Soldadura",
                  "Vazios (Voids)", "Bolhas de Ar", "Peça Incompleta",
                  "Deformação (Warping)", "Riscos", "Manchas", "Outro"
                ].map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setRejectDefects(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                    className={`text-left text-xs px-3 py-2 rounded-md border transition-all ${rejectDefects.includes(d) ? "bg-red-100 border-red-500 text-red-800 font-bold" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`defect-${d.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    {rejectDefects.includes(d) ? "✓ " : ""}{d}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Input value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} placeholder="Detalhes adicionais..." />
            </div>
            {rejectDefects.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs text-red-600 font-semibold">Resumo: {rejectQty} peça(s) rejeitada(s)</p>
                <p className="text-xs text-red-500 mt-1">Defeitos: {rejectDefects.join(", ")}</p>
              </div>
            )}
            <Button className="w-full bg-red-600 hover:bg-red-700" onClick={async () => {
              if (rejectDefects.length === 0) { toast.error("Selecione pelo menos um tipo de defeito"); return; }
              setSubmitting(true);
              try {
                await axios.post(`${API}/production-records`, {
                  order_id: active_order?.id,
                  good_quantity: 0,
                  rejected_quantity: rejectQty,
                  defect_type: rejectDefects.join(", "),
                  notes: `Rejeição: ${rejectQty} peças | Defeitos: ${rejectDefects.join(", ")}${rejectNotes ? ` | ${rejectNotes}` : ""}`,
                });
                toast.success(`${rejectQty} peça(s) rejeitada(s): ${rejectDefects.join(", ")}`);
                setShowRejectDialog(false); setRejectQty(1); setRejectDefects([]); setRejectNotes(""); onRefresh();
              } catch (e) { showError(e, "Erro ao registar rejeição"); }
              finally { setSubmitting(false); }
            }} disabled={submitting || rejectDefects.length === 0}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Confirmar Rejeição
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Técnico Qualidade Dialog */}
      <Dialog open={showTecQualidadeDialog} onOpenChange={setShowTecQualidadeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></svg>
              Inspeção Técnico de Qualidade
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Order Info */}
            <div className="bg-indigo-50 rounded-lg p-3 text-sm border border-indigo-200">
              <div className="flex justify-between">
                <div><span className="text-indigo-500">Ordem:</span> <strong>{active_order?.code || "-"}</strong></div>
                <div><span className="text-indigo-500">Máquina:</span> <strong>{machine?.code}</strong></div>
                <div><span className="text-indigo-500">Peça:</span> <strong>{part?.code} - {part?.description}</strong></div>
              </div>
              <div className="mt-1 text-xs text-indigo-400">Inspeção periódica (2 em 2 horas) — {new Date().toLocaleString("pt-PT")}</div>
            </div>

            {/* 1. Inspeção Visual e Estética */}
            <div className="border rounded-lg p-3">
              <p className="font-bold text-sm text-slate-800 mb-2 flex items-center gap-2">
                <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">1</span>
                Inspeção Visual e Estética
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "visual_cor", label: "Cor" },
                  { key: "visual_brilho", label: "Brilho" },
                  { key: "visual_impurezas", label: "Impurezas" },
                  { key: "visual_bolhas", label: "Bolhas" },
                  { key: "visual_manchas", label: "Manchas" },
                  { key: "visual_rebarbas", label: "Rebarbas" },
                  { key: "visual_preenchimento", label: "Preenchimento" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5">
                    <span className="text-xs text-slate-600">{label}</span>
                    <div className="flex gap-1">
                      {["OK", "NOK", "N/A"].map(v => (
                        <button key={v} type="button" onClick={() => setTecQualData(p => ({...p, [key]: v}))}
                          className={`text-[10px] px-2 py-0.5 rounded font-bold border ${tecQualData[key] === v ? (v === "OK" ? "bg-green-500 text-white border-green-500" : v === "NOK" ? "bg-red-500 text-white border-red-500" : "bg-slate-400 text-white border-slate-400") : "bg-white text-slate-400 border-slate-200"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Dimensões e Espessura */}
            <div className="border rounded-lg p-3">
              <p className="font-bold text-sm text-slate-800 mb-2 flex items-center gap-2">
                <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">2</span>
                Dimensões e Espessura
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Espessura (mm)</Label>
                  <Input type="number" step="0.01" value={tecQualData.dim_espessura} onChange={e => setTecQualData(p => ({...p, dim_espessura: e.target.value}))} placeholder="Ex: 2.5" className="h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Diâmetro (mm)</Label>
                  <Input type="number" step="0.01" value={tecQualData.dim_diametro} onChange={e => setTecQualData(p => ({...p, dim_diametro: e.target.value}))} placeholder="Ex: 50.0" className="h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Comprimento (mm)</Label>
                  <Input type="number" step="0.01" value={tecQualData.dim_comprimento} onChange={e => setTecQualData(p => ({...p, dim_comprimento: e.target.value}))} placeholder="Ex: 120.0" className="h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Instrumento</Label>
                  <select value={tecQualData.dim_instrumento} onChange={e => setTecQualData(p => ({...p, dim_instrumento: e.target.value}))} className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs">
                    <option>Calibre</option><option>Paquímetro</option><option>Micrómetro</option><option>Comparador</option><option>Gabarito</option><option>Outro</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 3. Ensaios Mecânicos */}
            <div className="border rounded-lg p-3">
              <p className="font-bold text-sm text-slate-800 mb-2 flex items-center gap-2">
                <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">3</span>
                Ensaios Mecânicos
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "mec_tracao", label: "Resistência à Tração" },
                  { key: "mec_impacto", label: "Resistência ao Impacto" },
                  { key: "mec_estanqueidade", label: "Estanqueidade" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex flex-col items-center bg-slate-50 rounded px-3 py-2">
                    <span className="text-[10px] text-slate-500 text-center mb-1">{label}</span>
                    <div className="flex gap-1">
                      {["OK", "NOK", "N/A"].map(v => (
                        <button key={v} type="button" onClick={() => setTecQualData(p => ({...p, [key]: v}))}
                          className={`text-[10px] px-2 py-0.5 rounded font-bold border ${tecQualData[key] === v ? (v === "OK" ? "bg-green-500 text-white border-green-500" : v === "NOK" ? "bg-red-500 text-white border-red-500" : "bg-slate-400 text-white border-slate-400") : "bg-white text-slate-400 border-slate-200"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Peso e Densidade */}
            <div className="border rounded-lg p-3">
              <p className="font-bold text-sm text-slate-800 mb-2 flex items-center gap-2">
                <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">4</span>
                Peso e Densidade
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Peso da Amostra (g)</Label>
                  <Input type="number" step="0.01" value={tecQualData.peso_valor} onChange={e => setTecQualData(p => ({...p, peso_valor: e.target.value}))} placeholder={`Nominal: ${part?.weight || "?"} g`} className="h-8 text-xs" />
                </div>
                <div className="flex flex-col items-center bg-slate-50 rounded px-3 py-2">
                  <span className="text-[10px] text-slate-500 mb-1">Uniformidade do Peso</span>
                  <div className="flex gap-1">
                    {["OK", "NOK", "N/A"].map(v => (
                      <button key={v} type="button" onClick={() => setTecQualData(p => ({...p, peso_uniforme: v}))}
                        className={`text-[10px] px-2 py-0.5 rounded font-bold border ${tecQualData.peso_uniforme === v ? (v === "OK" ? "bg-green-500 text-white border-green-500" : v === "NOK" ? "bg-red-500 text-white border-red-500" : "bg-slate-400 text-white border-slate-400") : "bg-white text-slate-400 border-slate-200"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Propriedades Químicas/Físicas */}
            <div className="border rounded-lg p-3">
              <p className="font-bold text-sm text-slate-800 mb-2 flex items-center gap-2">
                <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">5</span>
                Propriedades Químicas / Físicas
              </p>
              <div className="space-y-1">
                <Label className="text-xs">Índice de Fluidez (MFI g/10min)</Label>
                <Input type="number" step="0.1" value={tecQualData.quim_fluidez} onChange={e => setTecQualData(p => ({...p, quim_fluidez: e.target.value}))} placeholder="Ex: 12.5" className="h-8 text-xs" />
              </div>
            </div>

            {/* Resultado Global */}
            <div className="border-2 rounded-lg p-3">
              <p className="font-bold text-sm text-slate-800 mb-2">Resultado Global da Inspeção</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setTecQualData(p => ({...p, resultado_global: "conforme"}))}
                  className={`flex-1 py-3 rounded-lg font-bold text-sm border-2 transition-all ${tecQualData.resultado_global === "conforme" ? "bg-green-500 text-white border-green-500" : "bg-white text-slate-400 border-slate-200"}`}>
                  CONFORME
                </button>
                <button type="button" onClick={() => setTecQualData(p => ({...p, resultado_global: "nao_conforme"}))}
                  className={`flex-1 py-3 rounded-lg font-bold text-sm border-2 transition-all ${tecQualData.resultado_global === "nao_conforme" ? "bg-red-500 text-white border-red-500" : "bg-white text-slate-400 border-slate-200"}`}>
                  NÃO CONFORME
                </button>
                <button type="button" onClick={() => setTecQualData(p => ({...p, resultado_global: "condicional"}))}
                  className={`flex-1 py-3 rounded-lg font-bold text-sm border-2 transition-all ${tecQualData.resultado_global === "condicional" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-400 border-slate-200"}`}>
                  CONDICIONAL
                </button>
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-1">
              <Label className="text-xs">Observações Gerais</Label>
              <Input value={tecQualData.observacoes} onChange={e => setTecQualData(p => ({...p, observacoes: e.target.value}))} placeholder="Notas adicionais do técnico..." />
            </div>

            {/* Submit */}
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={async () => {
              setSubmitting(true);
              try {
                const visual = ["visual_cor","visual_brilho","visual_impurezas","visual_bolhas","visual_manchas","visual_rebarbas","visual_preenchimento"].map(k => `${k.replace("visual_","")}: ${tecQualData[k] || "N/A"}`).join(", ");
                const dims = [tecQualData.dim_espessura && `Esp:${tecQualData.dim_espessura}mm`, tecQualData.dim_diametro && `Diam:${tecQualData.dim_diametro}mm`, tecQualData.dim_comprimento && `Comp:${tecQualData.dim_comprimento}mm`].filter(Boolean).join(" | ") || "N/A";
                const mec = ["mec_tracao","mec_impacto","mec_estanqueidade"].map(k => `${k.replace("mec_","")}: ${tecQualData[k] || "N/A"}`).join(", ");
                const peso = tecQualData.peso_valor ? `${tecQualData.peso_valor}g (${tecQualData.peso_uniforme || "N/A"})` : "N/A";
                const resultado = tecQualData.resultado_global === "conforme" ? "CONFORME" : tecQualData.resultado_global === "nao_conforme" ? "NÃO CONFORME" : "CONDICIONAL";

                await axios.post(`${API}/quality-inspections`, {
                  order_id: active_order?.id,
                  order_code: active_order?.code || "",
                  machine_id: machine?.id,
                  machine_code: machine?.code || "",
                  part_code: part?.code || "",
                  part_description: part?.description || "",
                  mold_code: mold?.code || "",
                  visual: { cor: tecQualData.visual_cor, brilho: tecQualData.visual_brilho, impurezas: tecQualData.visual_impurezas, bolhas: tecQualData.visual_bolhas, manchas: tecQualData.visual_manchas, rebarbas: tecQualData.visual_rebarbas, preenchimento: tecQualData.visual_preenchimento },
                  dimensions: { espessura: tecQualData.dim_espessura, diametro: tecQualData.dim_diametro, comprimento: tecQualData.dim_comprimento, instrumento: tecQualData.dim_instrumento },
                  mechanical: { tracao: tecQualData.mec_tracao, impacto: tecQualData.mec_impacto, estanqueidade: tecQualData.mec_estanqueidade },
                  weight: { valor: tecQualData.peso_valor, uniforme: tecQualData.peso_uniforme },
                  chemical: { fluidez: tecQualData.quim_fluidez },
                  resultado_global: tecQualData.resultado_global,
                  observacoes: tecQualData.observacoes,
                });

                toast.success(`Inspeção registada: ${resultado}`);
                setShowTecQualidadeDialog(false);
                setTecQualData({ visual_cor:"",visual_brilho:"",visual_impurezas:"",visual_bolhas:"",visual_manchas:"",visual_rebarbas:"",visual_preenchimento:"", dim_espessura:"",dim_diametro:"",dim_comprimento:"",dim_instrumento:"Calibre", mec_tracao:"",mec_impacto:"",mec_estanqueidade:"", peso_valor:"",peso_uniforme:"", quim_fluidez:"", resultado_global:"conforme", observacoes:"" });
                onRefresh();
              } catch (e) { toast.error("Erro ao registar inspeção"); }
              finally { setSubmitting(false); }
            }} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Registar Inspeção de Qualidade
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Weight Verification Dialog (Início Turno) */}
      <Dialog open={showShiftWeightDialog} onOpenChange={setShowShiftWeightDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Scale className="h-5 w-5" />
              Verificação de Peso — Início de Turno
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {part && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <div className="font-bold text-amber-900">{part.code} — {part.description}</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div>Peso teórico 1 peça: <strong>{(part.unit_weight || 0).toFixed(2)} g</strong></div>
                  <div>Peso teórico 10 peças: <strong>{(part.weight_10_pieces || part.unit_weight * 10 || 0).toFixed(2)} g</strong></div>
                  <div>Tolerância: <strong>± {(part.weight_tolerance_pct || 5).toFixed(1)}%</strong></div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Peso 1 peça (g) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={shiftWeight1}
                  onChange={e => setShiftWeight1(e.target.value)}
                  placeholder="Ex: 1.23"
                  className="text-lg font-mono"
                  data-testid="shift-weight-1"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Peso 10 peças (g) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={shiftWeight10}
                  onChange={e => setShiftWeight10(e.target.value)}
                  placeholder="Ex: 12.30"
                  className="text-lg font-mono"
                  data-testid="shift-weight-10"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Input
                value={shiftWeightNotes}
                onChange={e => setShiftWeightNotes(e.target.value)}
                placeholder="Observações..."
                data-testid="shift-weight-notes"
              />
            </div>
            <Button
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={submitting || !shiftWeight1 || !shiftWeight10}
              onClick={async () => {
                if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
                setSubmitting(true);
                try {
                  await axios.post(`${API}/shift-weight-verification`, {
                    order_id: active_order.id,
                    weight_1_piece: parseFloat(shiftWeight1),
                    weight_10_pieces: parseFloat(shiftWeight10),
                    notes: shiftWeightNotes || null,
                  });
                  toast.success("Pesos do turno registados");
                  setShowShiftWeightDialog(false);
                  setShiftWeight1(""); setShiftWeight10(""); setShiftWeightNotes("");
                  onRefresh();
                } catch (e) { showError(e, "Erro ao registar pesos"); }
                finally { setSubmitting(false); }
              }}
              data-testid="shift-weight-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirmar Registo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Box Label Dialog (Etiqueta de Caixa com QR) */}
      <Dialog open={showBoxLabelDialog} onOpenChange={setShowBoxLabelDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <QrCode className="h-5 w-5" />
              Etiqueta de Caixa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {part && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <div className="font-bold">{part.code} — {part.description}</div>
                <div className="text-xs text-slate-600 mt-1">
                  Critério: {part.units_per_bag || 250} un/saco × {part.bags_per_box || 12} sacos = <strong>{(part.units_per_bag || 250) * (part.bags_per_box || 12)} un/caixa</strong>
                </div>
                {active_order?.code && <div className="text-xs text-slate-600 mt-0.5">Ordem: <strong>{active_order.code}</strong></div>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Quantidade (un) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  value={boxQty}
                  onChange={e => setBoxQty(e.target.value)}
                  className="text-lg font-mono"
                  data-testid="box-label-qty"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Peso bruto (kg) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={boxWeight}
                  onChange={e => setBoxWeight(e.target.value)}
                  className="text-lg font-mono"
                  data-testid="box-label-weight"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Input
                value={boxNotes}
                onChange={e => setBoxNotes(e.target.value)}
                placeholder="Lote especial, observações..."
                data-testid="box-label-notes"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              💡 Etiqueta com nº sequencial, lote (data + ordem) e QR Code. Impressão A4 com <strong>12 etiquetas por folha</strong>.
            </div>
            <Button
              className="w-full bg-slate-800 hover:bg-slate-900"
              disabled={submitting || !boxQty || !boxWeight}
              onClick={async () => {
                if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
                setSubmitting(true);
                try {
                  const res = await axios.post(`${API}/orders/${active_order.id}/box-labels`, {
                    quantity: parseInt(boxQty),
                    weight_kg: parseFloat(boxWeight),
                    notes: boxNotes || null,
                  });
                  toast.success(`Caixa #${res.data.box_number} criada (lote ${res.data.lot_number})`);
                  setShowBoxLabelDialog(false);
                  openBoxLabel(res.data, { autoPrint: true });
                } catch (e) { showError(e, "Erro ao criar etiqueta"); }
                finally { setSubmitting(false); }
              }}
              data-testid="box-label-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <QrCode className="h-4 w-4 mr-2" />}
              Gerar 1 Etiqueta e Imprimir
            </Button>

            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                disabled={submitting}
                onClick={async () => {
                  if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
                  setSubmitting(true);
                  try {
                    const res = await axios.get(`${API}/orders/${active_order.id}/box-labels`);
                    const labels = res.data || [];
                    if (labels.length === 0) { toast.error("Sem etiquetas criadas ainda"); return; }
                    setShowBoxLabelDialog(false);
                    openBoxLabels(labels, { autoPrint: true });
                    toast.success(`A imprimir ${labels.length} etiqueta(s)`);
                  } catch (e) { showError(e, "Erro a listar etiquetas"); }
                  finally { setSubmitting(false); }
                }}
                data-testid="box-label-print-all"
              >
                🖨️ Imprimir Todas
              </Button>

              <Button
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                disabled={submitting || !boxQty || !boxWeight}
                onClick={async () => {
                  if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
                  const targetTotal = active_order?.good_quantity_target || 0;
                  const perBox = parseInt(boxQty) || 1;
                  const need = Math.max(1, Math.ceil(targetTotal / perBox));
                  if (!window.confirm(`Vai criar ${need} etiqueta(s) para toda a produção (${targetTotal.toLocaleString("pt-PT")} un ÷ ${perBox} un/caixa). Continuar?`)) return;
                  setSubmitting(true);
                  try {
                    const res = await axios.post(`${API}/orders/${active_order.id}/box-labels/bulk`, {
                      quantity_per_box: perBox,
                      weight_kg_per_box: parseFloat(boxWeight),
                    });
                    const labels = res.data || [];
                    toast.success(`${labels.length} etiquetas criadas`);
                    setShowBoxLabelDialog(false);
                    openBoxLabels(labels, { autoPrint: true });
                  } catch (e) { showError(e, "Erro ao criar etiquetas em massa"); }
                  finally { setSubmitting(false); }
                }}
                data-testid="box-label-bulk-create"
              >
                📦 Gerar p/ Produção Toda
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Startup Checklist Dialog (Pre-Arranque + Aprovacao 1a Amostra) */}
      <Dialog open={showChecklistDialog} onOpenChange={setShowChecklistDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckSquare className="h-5 w-5" />
              Checklist de Arranque
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              {part && <div className="font-bold">{part.code} — {part.description}</div>}
              {active_order?.code && <div className="text-xs text-slate-600">Ordem: {active_order.code}</div>}
            </div>

            <div>
              <h3 className="font-bold text-slate-700 mb-2 text-sm">1. Verificações Antes do Arranque</h3>
              <div className="border rounded">
                {clPreItems.map((item, idx) => (
                  <div key={`pre-${idx}`} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-slate-50">
                    <span className="text-sm flex-1">{item.label}</span>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setClPreItems(prev => prev.map((it, i) => i === idx ? { ...it, ok: true } : it))}
                        className={`px-3 py-1 rounded text-xs font-bold ${item.ok ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}
                        data-testid={`cl-pre-ok-${idx}`}
                      >OK</button>
                      <button
                        onClick={() => setClPreItems(prev => prev.map((it, i) => i === idx ? { ...it, ok: false } : it))}
                        className={`px-3 py-1 rounded text-xs font-bold ${!item.ok ? "bg-red-500 text-white" : "bg-slate-200 text-slate-500"}`}
                        data-testid={`cl-pre-nok-${idx}`}
                      >NOK</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-slate-700 mb-2 text-sm">2. Aprovação da Primeira Amostra</h3>
              <div className="border rounded">
                {clSampleItems.map((item, idx) => (
                  <div key={`sa-${idx}`} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-slate-50">
                    <span className="text-sm flex-1">{item.label}</span>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setClSampleItems(prev => prev.map((it, i) => i === idx ? { ...it, ok: true } : it))}
                        className={`px-3 py-1 rounded text-xs font-bold ${item.ok ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}
                        data-testid={`cl-sa-ok-${idx}`}
                      >OK</button>
                      <button
                        onClick={() => setClSampleItems(prev => prev.map((it, i) => i === idx ? { ...it, ok: false } : it))}
                        className={`px-3 py-1 rounded text-xs font-bold ${!item.ok ? "bg-red-500 text-white" : "bg-slate-200 text-slate-500"}`}
                        data-testid={`cl-sa-nok-${idx}`}
                      >NOK</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Peso de 10 peças (g)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={clWeight10}
                  onChange={e => setClWeight10(e.target.value)}
                  placeholder={part?.weight_10_pieces?.toString() || "Ex: 12.30"}
                  data-testid="cl-weight-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Aprovado por</Label>
                <Input
                  value={clApprovedBy}
                  onChange={e => setClApprovedBy(e.target.value)}
                  placeholder="Nome/Código"
                  data-testid="cl-approved-by"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Input value={clNotes} onChange={e => setClNotes(e.target.value)} placeholder="Observações..." />
            </div>

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
              onClick={async () => {
                if (!active_order?.id) { toast.error("Sem ordem activa"); return; }
                setSubmitting(true);
                try {
                  await axios.post(`${API}/startup-checklist`, {
                    order_id: active_order.id,
                    pre_startup_items: clPreItems,
                    first_sample_items: clSampleItems,
                    weight_10_pieces: clWeight10 ? parseFloat(clWeight10) : null,
                    approved_by: clApprovedBy || null,
                    notes: clNotes || null,
                  });
                  const failed = [...clPreItems, ...clSampleItems].filter(i => !i.ok).length;
                  if (failed > 0) {
                    toast.warning(`Checklist registado mas com ${failed} item(s) NOK`);
                  } else {
                    toast.success("Checklist de arranque aprovado");
                  }
                  setShowChecklistDialog(false);
                  onRefresh();
                } catch (e) { showError(e, "Erro ao registar checklist"); }
                finally { setSubmitting(false); }
              }}
              data-testid="cl-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Registar Checklist
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ===== MAIN OPERATOR PANEL =====
export default function OperatorPanel() {
  const { user, clockInTime } = useAuth();
  const [machinesData, setMachinesData] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [shiftStatus, setShiftStatus] = useState(null);
  const [checklistDone, setChecklistDone] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklistSignature, setChecklistSignature] = useState(null);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [todayStats, setTodayStats] = useState({ good: 0, rejected: 0 });
  const [checklist, setChecklist] = useState({
    epi: false,
    area_limpa: false,
    maquina_visual: false,
    oleo_hidraulico: false,
    agua_refrigeracao: false,
    molde_fixado: false,
    material_funil: false,
    temp_zona: false,
    seguranca_portas: false,
    emergencia: false,
  });

  const fetchData = useCallback(async () => {
    try {
      const [factoryRes] = await Promise.all([
        axios.get(`${API}/dashboard/factory`),
      ]);

      const machines = factoryRes.data.machines || [];

      // Enrich with orders for each machine
      // Fetch all orders, parts and molds once
      let allOrders = [], allParts = [], allMolds = [];
      try {
        const [ordersRes, partsRes, moldsRes] = await Promise.all([
          axios.get(`${API}/orders`),
          axios.get(`${API}/parts`),
          axios.get(`${API}/molds`),
        ]);
        allOrders = ordersRes.data || [];
        allParts = partsRes.data || [];
        allMolds = moldsRes.data || [];
      } catch {}

      const enriched = await Promise.all(machines.map(async (m) => {
        try {
          const machineOrders = allOrders.filter(o => o.machine_id === m.machine.id);

          // Get stops for active order
          let stops = [];
          if (m.active_order) {
            try {
              const stopsRes = await axios.get(`${API}/stop-records?order_id=${m.active_order.id}`);
              stops = stopsRes.data || [];
            } catch {}
          }

          const enrichedOrders = machineOrders.map(o => {
            const p = allParts.find(p => p.id === o.part_id);
            const md = allMolds.find(md => md.id === o.mold_id);
            return { ...o, part_code: p?.code || "", part_description: p?.description || "", mold_code: md?.code || "" };
          });

          return {
            ...m,
            all_orders: enrichedOrders.filter(o => ["running","paused","planned","pending","ready"].includes(o.status)),
            completed_orders: enrichedOrders.filter(o => o.status === "completed"),
            completed_count: enrichedOrders.filter(o => o.status === "completed").length,
            total_orders: machineOrders.length,
            stops,
          };
        } catch {
          return { ...m, all_orders: [], completed_orders: [], completed_count: 0, total_orders: 0, stops: [] };
        }
      }));

      setMachinesData(enriched);

      // Update selected machine if viewing detail
      if (selectedMachine) {
        const updated = enriched.find(m => m.machine.id === selectedMachine.machine.id);
        if (updated) setSelectedMachine(updated);
      }
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [selectedMachine?.machine?.id]);

  useEffect(() => {
    fetchData();
    // Verificar se já tem turno activo E se ja fez a checklist hoje
    Promise.all([
      axios.get(`${API}/time-tracking/status`).catch(() => ({data: null})),
      axios.get(`${API}/operator-shifts/today-checklist`).catch(() => ({data: {done: false}})),
    ]).then(([statusRes, chkRes]) => {
      const clockedIn = statusRes.data?.clocked_in;
      const chkDone = chkRes.data?.done;
      if (clockedIn) {
        setShiftStatus("working");
      }
      if (chkDone) {
        setChecklistDone(true);
        const savedItems = chkRes.data?.checklist?.items || {};
        setChecklist(prev => ({ ...prev, ...savedItems }));
      } else if (user?.role === "operator" || user?.role === "shift_leader") {
        // So obrigatorio para operadores e chefes de turno (nao admin/display/quality)
        setChecklistDone(false);
        setShowChecklist(true);
      } else {
        setChecklistDone(true);  // outros roles: nao obrigatorio
      }
    });
    // Buscar histórico de turnos
    axios.get(`${API}/operator-shifts`).then(r => setShiftHistory(r.data || [])).catch(() => {});    const iv = setInterval(fetchData, 30000);
    const clock = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => { clearInterval(iv); clearInterval(clock); };
    // eslint-disable-next-line
  }, []);

  // Peças produzidas hoje pelo operador
  useEffect(() => {
    if (!user?.id) return;
    const loadToday = async () => {
      try {
        const r = await axios.get(`${API}/production-records?operator_id=${user.id}`);
        const todayStr = new Date().toISOString().slice(0, 10);
        const today = (r.data || []).filter(rec => (rec.recorded_at || "").slice(0, 10) === todayStr);
        setTodayStats({
          good: today.reduce((s, rec) => s + (rec.good_quantity || 0), 0),
          rejected: today.reduce((s, rec) => s + (rec.rejected_quantity || 0), 0),
        });
      } catch (_) { /* ignore */ }
    };
    loadToday();
    const iv = setInterval(loadToday, 60000);
    return () => clearInterval(iv);
  }, [user?.id]);

  // Auto-select machine via ?machine=<code> URL param (útil para QR das máquinas)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const machineCode = params.get("machine");
    if (!machineCode || !machinesData?.length || selectedMachine) return;
    const found = machinesData.find(m => m.machine.code === machineCode || m.machine.id === machineCode);
    if (found) setSelectedMachine(found);
  }, [machinesData, selectedMachine]);

  // Duração do turno actual (em horas decimais)
  const shiftHours = clockInTime ? (currentTime.getTime() - new Date(clockInTime).getTime()) / 3600000 : 0;
  const shiftH = Math.floor(Math.max(0, shiftHours));
  const shiftM = Math.floor((Math.max(0, shiftHours) - shiftH) * 60);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Detail View
  if (selectedMachine) {
    return (
      <div className="h-[calc(100vh-120px)]">
        <MachineDetail
          data={selectedMachine}
          onBack={() => setSelectedMachine(null)}
          onRefresh={fetchData}
        />
      </div>
    );
  }

  // Grid View
  const handleShiftAction = async (action) => {
    try {
      if (action === "start") {
        try { await axios.post(`${API}/time-tracking/clock-in`); } catch (e) {}
        setShiftStatus("working");
        setShowChecklist(true);
        toast.success("Turno iniciado! Complete a checklist.");
      } else if (action === "end") {
        await axios.post(`${API}/time-tracking/clock-out`);
        setShiftStatus(null);
        setChecklistDone(false);
        // Refresh history
        axios.get(`${API}/operator-shifts`).then(r => setShiftHistory(r.data || [])).catch(() => {});
        toast.success("Turno terminado!");
      } else if (action === "lunch") {
        await axios.post(`${API}/operator-shifts/break`, { type: "almoço", action: "start" });
        setShiftStatus("lunch");
        toast.success("Pausa para almoço registada");
      } else if (action === "break") {
        await axios.post(`${API}/operator-shifts/break`, { type: "lanche", action: "start" });
        setShiftStatus("break");
        toast.success("Pausa para lanche registada");
      } else if (action === "resume") {
        await axios.post(`${API}/operator-shifts/break`, { type: "pausa", action: "end" });
        setShiftStatus("working");
        toast.success("Pausa terminada, de volta ao trabalho!");
      }
    } catch (e) {
      showError(e, "Erro");
    }
  };

  const allChecklistDone = Object.values(checklist).every(v => v);

  const handleChecklistSubmit = async () => {
    if (!allChecklistDone) { toast.error("Complete todos os itens da checklist"); return; }
    if (!checklistSignature) { toast.error("Assine antes de submeter"); return; }
    try {
      await axios.post(`${API}/operator-shifts/shift-checklist`, { items: checklist, signature: checklistSignature });
      setChecklistDone(true);
      setShowChecklist(false);
      toast.success("Checklist assinada! Já pode iniciar produção.");
    } catch (e) {
      showError(e, "Erro ao registar checklist");
    }
  };

  const checklistItems = [
    { key: "epi", label: "EPIs colocados (óculos, luvas, calçado de segurança)" },
    { key: "area_limpa", label: "Área de trabalho limpa e organizada" },
    { key: "maquina_visual", label: "Inspeção visual da máquina (sem fugas, danos visíveis)" },
    { key: "oleo_hidraulico", label: "Nível de óleo hidráulico verificado" },
    { key: "agua_refrigeracao", label: "Sistema de refrigeração/água verificado" },
    { key: "molde_fixado", label: "Molde corretamente fixado e centrado" },
    { key: "material_funil", label: "Material carregado no funil/secador" },
    { key: "temp_zona", label: "Temperaturas das zonas de aquecimento verificadas" },
    { key: "seguranca_portas", label: "Portas de segurança a funcionar corretamente" },
    { key: "emergencia", label: "Botão de emergência testado e acessível" },
  ];

  return (
    <div className="space-y-6" data-testid="operator-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <OperatorAvatar photo={user?.photo_url} name={user?.name} code={user?.user_code} size="xl" />
          <div>
            <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3">
              <Factory className="h-7 w-7" />
              Monitor de Operador
            </h1>
            <p className="text-muted-foreground mt-1">
              {user?.name || user?.user_code} — Selecione uma máquina para operar
            </p>
            {/* Badges: horas turno + peças hoje */}
            <div className="flex gap-2 mt-2 flex-wrap">
              {clockInTime && (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-md text-xs font-bold" data-testid="op-shift-hours">
                  <Clock className="h-3.5 w-3.5" /> Turno: {shiftH}h{String(shiftM).padStart(2, "0")}
                </span>
              )}
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded-md text-xs font-bold" data-testid="op-today-good">
                ✓ Hoje: {todayStats.good.toLocaleString()} boas
              </span>
              {todayStats.rejected > 0 && (
                <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 px-2 py-1 rounded-md text-xs font-bold" data-testid="op-today-rejected">
                  ✗ {todayStats.rejected.toLocaleString()} rejeitadas
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-2" /> Atualizar</Button>
          <div className="text-right">
            <p className="font-mono text-2xl font-bold text-foreground">{currentTime.toLocaleTimeString("pt-PT")}</p>
            <p className="text-xs text-muted-foreground">{currentTime.toLocaleDateString("pt-PT", { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
        </div>
      </div>

      {/* Shift Controls */}
      <Card className="border-2 border-amber-200 bg-amber-50/30">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-bold text-sm text-foreground">Controlo de Turno</p>
                <p className="text-xs text-muted-foreground">
                  {shiftStatus === "working" && (checklistDone ? "Em serviço — Checklist concluída" : "Em serviço — Checklist pendente")}
                  {shiftStatus === "lunch" && "Pausa para almoço"}
                  {shiftStatus === "break" && "Pausa para lanche"}
                  {!shiftStatus && "Turno não iniciado"}
                </p>
              </div>
              {shiftStatus && (
                <Badge className={`${shiftStatus === "working" ? "bg-green-500" : "bg-amber-500"} text-white`}>
                  {shiftStatus === "working" ? "ATIVO" : "EM PAUSA"}
                </Badge>
              )}
              {shiftStatus === "working" && checklistDone && (
                <Badge className="bg-blue-500 text-white">CHECKLIST OK</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!shiftStatus && (
                <Button className="bg-green-600 hover:bg-green-700 gap-2" onClick={() => handleShiftAction("start")} data-testid="shift-start">
                  <Play className="h-4 w-4" /> Início Turno
                </Button>
              )}
              {shiftStatus === "working" && !checklistDone && (
                <Button className="bg-blue-600 hover:bg-blue-700 gap-2 animate-pulse" onClick={() => setShowChecklist(true)} data-testid="shift-checklist">
                  <CheckCircle2 className="h-4 w-4" /> Fazer Checklist
                </Button>
              )}
              {shiftStatus === "working" && (
                <>
                  <Button variant="outline" className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-100" onClick={() => handleShiftAction("lunch")} data-testid="shift-lunch">
                    <Pause className="h-4 w-4" /> Almoço
                  </Button>
                  <Button variant="outline" className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-100" onClick={() => handleShiftAction("break")} data-testid="shift-break">
                    <Pause className="h-4 w-4" /> Lanche
                  </Button>
                  <Button className="bg-red-600 hover:bg-red-700 gap-2" onClick={() => handleShiftAction("end")} data-testid="shift-end">
                    <Square className="h-4 w-4" /> Fim Turno
                  </Button>
                </>
              )}
              {(shiftStatus === "lunch" || shiftStatus === "break") && (
                <Button className="bg-green-600 hover:bg-green-700 gap-2" onClick={() => handleShiftAction("resume")} data-testid="shift-resume">
                  <Play className="h-4 w-4" /> Voltar ao Trabalho
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-muted-foreground">Total Máquinas</p><p className="text-3xl font-bold">{machinesData.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-green-600">Em Execução</p><p className="text-3xl font-bold text-green-600">{machinesData.filter(m => m.machine.status === "running").length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-amber-600">Paradas</p><p className="text-3xl font-bold text-amber-600">{machinesData.filter(m => ["idle","stopped","paused"].includes(m.machine.status)).length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-purple-600">Manutenção</p><p className="text-3xl font-bold text-purple-600">{machinesData.filter(m => m.machine.status === "maintenance").length}</p></CardContent></Card>
      </div>

      {/* Machine Grid — blocked if checklist not done */}
      {!checklistDone && shiftStatus === "working" ? (
        <Card className="border-2 border-amber-300 bg-amber-50">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <h3 className="text-xl font-bold text-amber-800">Checklist de Início de Turno Pendente</h3>
            <p className="text-amber-600 mt-2">Complete a checklist de segurança antes de operar qualquer máquina.</p>
            <Button className="mt-4 bg-blue-600 hover:bg-blue-700 gap-2" onClick={() => setShowChecklist(true)}>
              <CheckCircle2 className="h-4 w-4" /> Iniciar Checklist
            </Button>
          </CardContent>
        </Card>
      ) : !shiftStatus ? (
        <Card className="border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-xl font-bold text-slate-600">Turno Não Iniciado</h3>
            <p className="text-slate-400 mt-2">Clique "Início Turno" para começar.</p>
          </CardContent>
        </Card>
      ) : (
        <MachineGrid machines={machinesData} onSelect={setSelectedMachine} />
      )}

      {/* Shift History Table (22 days) */}
      {shiftHistory.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <p className="font-bold text-sm text-foreground mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Histórico de Turnos (últimos 22 dias)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Data</th>
                    <th className="text-left px-3 py-2 font-semibold">Entrada</th>
                    <th className="text-left px-3 py-2 font-semibold">Saída</th>
                    <th className="text-left px-3 py-2 font-semibold">Pausas</th>
                    <th className="text-right px-3 py-2 font-semibold">Total Pausas</th>
                    <th className="text-right px-3 py-2 font-semibold">Total Horas</th>
                    <th className="text-right px-3 py-2 font-semibold">Horas Trabalhadas</th>
                  </tr>
                </thead>
                <tbody>
                  {shiftHistory.map((s, i) => (
                    <tr key={s.id || i} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono font-bold">{s.date ? new Date(s.date).toLocaleDateString("pt-PT", {weekday:"short", day:"2-digit", month:"2-digit"}) : "-"}</td>
                      <td className="px-3 py-2 font-mono text-green-600">{s.clock_in ? new Date(s.clock_in).toLocaleTimeString("pt-PT", {hour:"2-digit", minute:"2-digit"}) : "-"}</td>
                      <td className="px-3 py-2 font-mono text-red-600">{s.clock_out ? new Date(s.clock_out).toLocaleTimeString("pt-PT", {hour:"2-digit", minute:"2-digit"}) : <Badge className="bg-green-500 text-white text-[10px]">ATIVO</Badge>}</td>
                      <td className="px-3 py-2">
                        {s.breaks && s.breaks.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {s.breaks.map((b, bi) => (
                              <Badge key={bi} variant="outline" className="text-[10px]">{b.type} ({b.duration_min}min)</Badge>
                            ))}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-amber-600">{s.total_break_min > 0 ? `${s.total_break_min} min` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.total_hours > 0 ? `${s.total_hours}h` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-green-600">{s.worked_hours > 0 ? `${s.worked_hours}h` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-slate-50 font-bold">
                    <td className="px-3 py-2" colSpan={4}>Total ({shiftHistory.filter(s => s.clock_out).length} turnos)</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-600">{shiftHistory.reduce((s, r) => s + (r.total_break_min || 0), 0)} min</td>
                    <td className="px-3 py-2 text-right font-mono">{shiftHistory.reduce((s, r) => s + (r.total_hours || 0), 0).toFixed(1)}h</td>
                    <td className="px-3 py-2 text-right font-mono text-green-600">{shiftHistory.reduce((s, r) => s + (r.worked_hours || 0), 0).toFixed(1)}h</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Checklist Dialog - OBRIGATORIO, nao dispensavel */}
      <Dialog open={showChecklist} onOpenChange={(open) => {
        // So permite fechar se ja submeteu (checklistDone=true)
        if (!open && !checklistDone) {
          toast.error("A checklist é OBRIGATÓRIA. Complete todos os itens para continuar.");
          return;
        }
        setShowChecklist(open);
      }}>
        <DialogContent
          className="max-w-lg"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="shift-checklist-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <CheckCircle2 className="h-5 w-5" />
              Checklist de Início de Turno (Obrigatória)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-xs text-amber-800">
              ⚠ Esta checklist é <strong>OBRIGATÓRIA</strong> e faz parte dos requisitos ISO 9001.
              Não pode operar máquinas antes de concluí-la.
            </div>
            <p className="text-sm text-muted-foreground">Verifique todos os itens antes de iniciar a operação:</p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {checklistItems.map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    checklist[key]
                      ? "bg-green-50 border-green-400"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`checklist-item-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={checklist[key]}
                    onChange={e => setChecklist(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="h-5 w-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                  />
                  <span className={`text-sm ${checklist[key] ? "text-green-800 font-medium" : "text-slate-600"}`}>
                    {label}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-sm text-muted-foreground">
                {Object.values(checklist).filter(Boolean).length}/{checklistItems.length} verificados
              </p>
              <Button
                className="bg-green-600 hover:bg-green-700 gap-2"
                onClick={handleChecklistSubmit}
                disabled={!allChecklistDone || !checklistSignature}
                data-testid="btn-submit-checklist"
              >
                <CheckCircle2 className="h-4 w-4" />
                {!allChecklistDone ? `Faltam ${checklistItems.length - Object.values(checklist).filter(Boolean).length} itens` : (!checklistSignature ? "Assine para confirmar" : "Confirmar e Iniciar")}
              </Button>
            </div>
            {allChecklistDone && (
              <div className="pt-2 border-t">
                <SignatureCanvas
                  label="Assinatura do Operador"
                  required
                  onChange={setChecklistSignature}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
